/**
 * Cashu Wallet Class.
 *
 * @remarks
 * This is the instantiation point for the Cashu-TS library.
 */

import { type AuthProvider } from '../auth/AuthProvider';
import {
  signMintQuote,
  findSigningKey,
  signP2PKProofs as cryptoSignP2PKProofs,
  hashToCurve,
  isP2PKSigAll,
  buildP2PKSigAllMessage,
  assertSigAllInputs,
  buildLegacyP2PKSigAllMessage,
  parseSecret,
} from '../crypto';
import { type Logger, NULL_LOGGER, fail, failIf, failIfNullish, safeCallback } from '../logger';
import { Mint } from '../mint';
import { Amount, type AmountLike } from '../model/Amount';
import { CTSError } from '../model/Errors';
import { MintInfo } from '../model/MintInfo';
import { OutputData, type OutputDataLike } from '../model/OutputData';
import { DefaultOutputDataCreator, type OutputDataCreator } from '../model/OutputDataCreator';
import type {
  GetInfoResponse,
  MeltRequest,
  MeltQuoteBaseResponse,
  MeltQuoteBolt11Request,
  MeltQuoteBolt11Response,
  MeltQuoteBolt12Response,
  MeltQuoteOnchainResponse,
  MintRequest,
  MintQuoteBaseResponse,
  MintQuoteBolt11Response,
  MintQuoteBolt12Response,
  MintQuoteOnchainResponse,
  MintQuoteBolt11Request,
  MintQuoteBolt12Request,
  SwapRequest,
} from '../model/types';
import type { SerializedBlindedSignature } from '../model/types/blinded';
import type { KeyChainCache } from '../model/types/keyset';
import { CheckStateEnum, type ProofState } from '../model/types/NUT07';
import { type BatchMintRequest } from '../model/types/NUT29';
import type { Proof, ProofLike } from '../model/types/proof';
import type { Token } from '../model/types/token';
import {
  getDecodedToken,
  hasValidDleq,
  invoiceHasAmountInHRP,
  normalizeProofAmounts,
  normalizeUrl,
  splitAmount,
  sumProofs,
  verifyDleqIfPresent,
  ABSOLUTE_MAX_BATCH_SIZE,
} from '../utils';

import { getKeepAmounts, stringifyOutputTypeForLog } from './_internal';
import {
  type CounterSource,
  EphemeralCounterSource,
  type OperationCounters,
  type CounterRange,
} from './CounterSource';
import { KeyChain } from './KeyChain';
import { type Keyset } from './Keyset';
import { selectProofsRGLI, type SelectProofs } from './SelectProofs';
import {
  type MeltPreview,
  type OutputType,
  type OutputConfig,
  type SendConfig,
  type SendOfflineConfig,
  type ReceiveConfig,
  type MintProofsConfig,
  type MeltProofsConfig,
  type PrepareMeltConfig,
  type CompleteMeltOptions,
  type SwapTransaction,
  type MeltProofsResponse,
  type SendResponse,
  type RestoreConfig,
  type SecretsPolicy,
  type SwapPreview,
  type MintPreview,
  type BatchMintPreview,
} from './types';
import { WalletCounters } from './WalletCounters';
import { WalletEvents } from './WalletEvents';
import { WalletOps } from './WalletOps';

// model helpers

const PENDING_KEYSET_ID = '__PENDING__';

/**
 * Class that represents a Cashu wallet.
 *
 * @remarks
 * This class should act as the entry point for this library. Can be instantiated with a mint
 * instance or mint url.
 * @example
 *
 * ```typescript
 * import { Wallet } from '@cashu/cashu-ts';
 * const wallet = new Wallet('http://localhost:3338', { unit: 'sat' });
 * await wallet.loadMint(); // Initialize mint info, keysets, and keys
 * // Wallet is now ready to use, eg:
 * const proofs = [...]; // your array of unspent proofs
 * const { keep, send } = await wallet.send(32, proofs);
 * ```
 */
class Wallet {
  /**
   * Mint instance - allows direct calls to the mint.
   */
  public readonly mint: Mint;
  /**
   * Entry point for the builder.
   *
   * @example
   *
   *     const { keep, send } = await wallet.ops
   *       .send(5, proofs)
   *       .asDeterministic() // counter: 0 = auto
   *       .keepAsRandom()
   *       .includeFees(true)
   *       .run();
   *
   *     const proofs = await wallet.ops
   *       .receive(token)
   *       .asDeterministic()
   *       .keyset(wallet.keysetId)
   *       .run();
   */
  public readonly ops: WalletOps;
  /**
   * Convenience wrapper for events.
   */
  public readonly on: WalletEvents;
  /**
   * Developer-friendly counters API.
   */
  public readonly counters: WalletCounters;
  private _keyChain: KeyChain;
  private _seed: Uint8Array | undefined = undefined;
  private _unit = 'sat';
  private _mintInfo: MintInfo | undefined = undefined;
  private _denominationTarget = 3;
  private _secretsPolicy: SecretsPolicy = 'auto';
  private _counterSource: CounterSource;
  private _boundKeysetId: string = PENDING_KEYSET_ID;
  private _selectProofs: SelectProofs;
  private _outputDataCreator: OutputDataCreator;
  private _requireSigDleq = false;
  private _logger: Logger;

  /**
   * Create a wallet for a given mint and unit. Call `loadMint` before use.
   *
   * Binding, if `options.keysetId` is omitted, the wallet binds to the cheapest active keyset for
   * this unit during `loadMint`.
   *
   * Caching, preload mint info and keychain data by calling `loadMintFromCache` after construction.
   *
   * The keychain stores all loaded keysets and filters query results by the wallet unit.
   *
   * Deterministic secrets, pass `bip39seed` and optionally `secretsPolicy`. Deterministic outputs
   * reserve counters from `counterSource`, or an ephemeral in memory source if not supplied.
   * `initialCounter` applies only with a supplied `keysetId` and the ephemeral source.
   *
   * Splitting, `denominationTarget` guides proof splits, default is 3. Override coin selection with
   * `selectProofs` if needed. Logging defaults to a null logger.
   *
   * @param mint Mint instance or URL.
   * @param options Optional settings.
   * @param options.unit Wallet unit, default 'sat'.
   * @param options.keysetId Bind to this keyset id, else bind on `loadMint`.
   * @param options.bip39seed BIP39 seed for deterministic secrets.
   * @param options.secretsPolicy Secrets policy, default 'auto'.
   * @param options.counterSource Counter source for deterministic outputs. If provided, this takes
   *   precedence over counterInit. Use when you need persistence across processes or devices.
   * @param options.counterInit Seed values for the built-in EphemeralCounterSource. Ignored if
   *   counterSource is also provided.
   * @param options.denominationTarget Target proofs per denomination, default 3.
   * @param options.selectProofs Custom proof selection function.
   * @param options.outputDataCreator Custom OutputDataCreator implementation. The canonical and
   *   maintained implementation is the default Noble Curves based behavior exposed by
   *   `OutputData.create*()`. Custom creators are an escape hatch for runtime-specific needs, and
   *   compatibility and maintenance are the integrator's responsibility.
   * @param options.requireSigDleq Fail mint/swap/melt responses when the mint advertises NUT-12
   *   support but omits DLEQ proofs on returned blinded signatures. This is a fail-fast consistency
   *   check, not protection against a malicious mint already consuming inputs or payments.
   * @param options.logger Logger instance, default null logger.
   */
  constructor(
    mint: Mint | string,
    options?: {
      unit?: string;
      authProvider?: AuthProvider;
      keysetId?: string; // if omitted, wallet binds to cheapest in loadMint
      bip39seed?: Uint8Array;
      secretsPolicy?: SecretsPolicy; // optional, auto
      counterSource?: CounterSource; // optional, otherwise ephemeral
      counterInit?: Record<string, number>; // optional, starting "next" per keyset
      denominationTarget?: number;
      selectProofs?: SelectProofs; // optional override
      outputDataCreator?: OutputDataCreator;
      requireSigDleq?: boolean;
      logger?: Logger;
    },
  ) {
    this.ops = new WalletOps(this);
    this.on = new WalletEvents(this);
    this._logger = options?.logger ?? NULL_LOGGER; // init early (seed can throw)
    this._selectProofs = options?.selectProofs ?? selectProofsRGLI; // vital
    this._outputDataCreator = options?.outputDataCreator ?? new DefaultOutputDataCreator();
    this.mint =
      typeof mint === 'string'
        ? new Mint(mint, { authProvider: options?.authProvider, logger: this._logger })
        : mint;
    this._unit = options?.unit ?? this._unit;
    this._boundKeysetId = options?.keysetId ?? this._boundKeysetId;
    if (options?.bip39seed) {
      this.failIf(
        !(options.bip39seed instanceof Uint8Array),
        'bip39seed must be a valid Uint8Array',
        {
          bip39seed: options.bip39seed,
        },
      );
      this._seed = options.bip39seed;
    }
    this._secretsPolicy = options?.secretsPolicy ?? this._secretsPolicy;
    if (options?.counterSource) {
      this._counterSource = options.counterSource;
    } else {
      this._counterSource = new EphemeralCounterSource(options?.counterInit);
    }
    this.counters = new WalletCounters(this._counterSource);
    this._keyChain = new KeyChain(this.mint, this._unit);
    this._denominationTarget = options?.denominationTarget ?? this._denominationTarget;
    this._requireSigDleq = options?.requireSigDleq ?? this._requireSigDleq;
  }

  // Convenience wrappers for "log and throw"
  private fail(message: string, context?: Record<string, unknown>): never {
    return fail(message, this._logger, context);
  }
  private failIf(
    condition: boolean,
    message: string,
    context?: Record<string, unknown>,
  ): asserts condition is false {
    return failIf(condition, message, this._logger, context);
  }
  private failIfNullish<T>(
    value: T,
    message: string,
    context?: Record<string, unknown>,
  ): asserts value is Exclude<T, null | undefined> {
    return failIfNullish(value, message, this._logger, context);
  }
  private requireSupport(op: 'mint' | 'melt', method: string): void {
    this.failIf(
      !this.getMintInfo().supportsMintMeltMethod(op, method, this._unit),
      `Mint does not support ${method} ${op} for unit '${this._unit}'`,
    );
  }
  private safeCallback<T>(
    cb: ((p: T) => void) | undefined,
    payload: T,
    context?: Record<string, unknown>,
  ): void {
    safeCallback(cb, payload, this._logger, context);
  }

  /**
   * Parses AmountLike to Amount.
   */
  private parseAmount(amount: AmountLike, op: string, allowZero = false): Amount {
    try {
      const parsed = Amount.from(amount);
      if (!allowZero) {
        this.failIf(parsed.isZero(), `Amount must be positive: ${parsed.toString()}`, {
          op,
          amount,
        });
      }
      return parsed;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      this._logger.error(message, { op, amount });
      throw new CTSError(message, { cause: e });
    }
  }

  /**
   * Load mint information, keysets, and keys.
   *
   * @remarks
   * Must be called before using other methods, unless loading mint from cache. See:
   * `loadMintFromCache`.
   * @param forceRefresh If true, re-fetches data even if cached.
   * @throws If fetching mint info, keysets, or keys fails.
   */
  async loadMint(forceRefresh?: boolean): Promise<void> {
    const promises = [];

    // Load mint info
    if (!this._mintInfo || forceRefresh) {
      promises.push(
        this.mint.getInfo().then((info) => {
          this._mintInfo = new MintInfo(info, this._logger);
          this.mint.setMintInfo(this._mintInfo);
          return null;
        }),
      );
    }

    // Load KeyChain
    promises.push(this._keyChain.init(forceRefresh));

    await Promise.all(promises);
    this.finishInit();
  }

  /**
   * Load mint information, keysets, and keys from cached data.
   *
   * @remarks
   * Use this when you already have cached mint info and keychain cache and want to avoid network
   * calls.
   *
   * The `cache` argument should usually come from `wallet.keyChain.cache`.
   */
  loadMintFromCache(mintInfo: GetInfoResponse, cache: KeyChainCache): void {
    this._mintInfo = new MintInfo(mintInfo, this._logger);
    this.mint.setMintInfo(this._mintInfo);
    this._keyChain.loadFromCache(cache);
    this.finishInit();
  }

  /**
   * Finishes wiring up the wallet instance and checks we are "Go for launch".
   */
  private finishInit(): void {
    this._logger.debug('KeyChain', { keychain: this._keyChain.cache });

    // Go Keychain?
    if (this._boundKeysetId === PENDING_KEYSET_ID) {
      try {
        // Bind the cheapest active keyset
        this._boundKeysetId = this._keyChain.getCheapestKeyset().id;
      } catch (e) {
        // Edge case: mint has no active keysets, so leave wallet unbound
        // May happen if a mint is closing down and unwinding liabilities
        this._logger.warn('No active keyset available, wallet remains unbound', {
          unit: this._unit,
          err: (e as Error).message,
        });
      }
    } else {
      // Keyset ID was bound in wallet constructor, so ensure it exists and unit
      // matches, but do NOT require keys yet. It may be an inactive keyset for
      // restore, and if so, keys will be fetched async later.
      const k = this._keyChain.getKeyset(this._boundKeysetId);
      this.failIf(k.unit !== this._unit, 'Keyset unit does not match wallet unit', {
        keyset: k.id,
        unit: k.unit,
        walletUnit: this._unit,
      });
    }

    // Go Mintinfo?
    this.getMintInfo();
  }

  // -----------------------------------------------------------------
  // Section: Getters
  // -----------------------------------------------------------------

  /**
   * Get the wallet's KeyChain.
   *
   * @returns The Keychain.
   */
  get keyChain(): KeyChain {
    return this._keyChain;
  }

  /**
   * Get the wallet's unit.
   *
   * @returns The unit (e.g., 'sat').
   */
  get unit(): string {
    return this._unit;
  }

  /**
   * Get information about the mint.
   *
   * @remarks
   * Returns cached mint info. Call `loadMint` first to initialize the wallet.
   * @returns Mint info.
   * @throws If mint info is not initialized.
   */
  getMintInfo(): MintInfo {
    this.failIfNullish(
      this._mintInfo,
      'Mint info not initialized; call loadMint or loadMintFromCache first',
    );
    return this._mintInfo;
  }

  /**
   * The keyset ID bound to this wallet instance.
   */
  get keysetId(): string {
    this.failIf(
      this._boundKeysetId === PENDING_KEYSET_ID,
      'Wallet has no bound keyset. The mint may have no active keysets, or wallet was not initialized via loadMint or loadMintFromCache',
    );
    return this._boundKeysetId;
  }

  /**
   * Gets the requested keyset or the keyset bound to the wallet.
   *
   * @remarks
   * This method enforces wallet policies. If `id` is omitted, it returns the keyset bound to this
   * wallet, including validation that:
   *
   * - The keyset exists in the keychain,
   * - The unit matches the wallet's unit,
   * - Keys are loaded for that keyset.
   *
   * Contrast with `keyChain.getKeyset(id?)`, which, when called without an id, returns the cheapest
   * active keyset for the unit, ignoring the wallet binding.
   * @param id Optional keyset id to resolve. If omitted, the wallet's bound keyset is used.
   * @returns The resolved `Keyset`.
   * @throws If the keyset is not found, has no keys, or its unit differs from the wallet.
   */
  public getKeyset(id?: string): Keyset {
    const keyset = this._keyChain.getKeyset(id ?? this.keysetId);
    this.failIf(keyset.unit !== this._unit, 'Keyset unit does not match wallet unit', {
      keyset: keyset.id,
      unit: keyset.unit,
      walletUnit: this._unit,
    });
    this.failIf(!keyset.hasKeys, 'Keyset has no keys loaded', { keyset: keyset.id });
    return keyset;
  }

  /**
   * Resolves the keyset used to create new proofs, enforcing the output policy.
   *
   * @remarks
   * Legacy (pre-v1, base64-id) keysets may be spent and restored, but never used to create new
   * proofs. Inactive keysets are rejected per NUT-02 — the mint would refuse to sign outputs on
   * them, so fail fast here. Unknown hex versions are already excluded upstream: their keys fail
   * verification and `getKeyset` rejects keysets without keys.
   *
   * Only the prepare-side ops use this gate. The `complete*` ops use plain `getKeyset` as the mint
   * will have signed.
   * @param id Optional keyset id to resolve. If omitted, the wallet's bound keyset is used.
   * @returns The resolved `Keyset`.
   * @throws If the keyset fails `getKeyset` validation, has a legacy (non-hex) id, or is inactive.
   */
  private getOutputKeyset(id?: string): Keyset {
    const keyset = this.getKeyset(id);
    this.failIf(!keyset.hasHexId, 'Legacy keyset cannot be used to create new proofs', {
      keyset: keyset.id,
    });
    this.failIf(!keyset.isActive, 'Inactive keyset cannot be used to create new proofs', {
      keyset: keyset.id,
    });
    return keyset;
  }

  /**
   * Asserts the mint has at least one usable (active, hex-id, keyed) keyset for the wallet's unit
   * before requesting a mint quote — a paid quote could otherwise never be redeemed for proofs.
   *
   * @param op Operation name for the error message.
   * @throws If the keychain is uninitialized or has no usable active keyset for the unit.
   */
  private requireMintableKeyset(op: string): void {
    try {
      this._keyChain.getCheapestKeyset();
    } catch (e) {
      this.fail(
        `${op}: no active keyset for unit '${this._unit}' — a paid mint quote could not be redeemed`,
        { reason: (e as Error).message },
      );
    }
  }

  public get logger(): Logger {
    return this._logger;
  }

  // -----------------------------------------------------------------
  // Section: Counters
  // -----------------------------------------------------------------

  private async reserveFor(keysetId: string, totalOutputs: number): Promise<CounterRange> {
    if (totalOutputs <= 0) return { start: 0, count: 0 };
    return this._counterSource.reserve(keysetId, totalOutputs);
  }

  private countersNeeded(ot: OutputType): number {
    if (ot.type !== 'deterministic' || ot.counter !== 0) return 0;
    return (ot.denominations ?? []).length;
  }

  private async addCountersToOutputTypes(
    keysetId: string,
    ...outputTypes: OutputType[]
  ): Promise<{ outputTypes: OutputType[]; used?: OperationCounters }> {
    // Get all outputTypes with a manual counter and denominations set.
    const manual = outputTypes.filter(
      (ot): ot is Extract<OutputType, { type: 'deterministic' }> =>
        ot.type === 'deterministic' && ot.counter > 0 && (ot.denominations?.length ?? 0) > 0,
    );

    // Reject if manual ranges overlap
    if (manual.length > 1) {
      const ranges = manual
        .map((ot) => ({
          start: ot.counter,
          end: ot.counter + ot.denominations!.length, // exclusive
        }))
        .sort((a, b) => a.start - b.start);
      for (let i = 1; i < ranges.length; i++) {
        this.failIf(ranges[i].start < ranges[i - 1].end, 'Manual counter ranges overlap', {
          keysetId,
          prev: ranges[i - 1],
          cur: ranges[i],
        });
      }
    }

    // If any deterministic OutputType has a manual counter (> 0), advance
    // the counter source so future "auto" allocations do not reuse counters.
    if (manual.length > 0) {
      // Get the max counter manually allocated
      const maxManualEnd = Math.max(...manual.map((ot) => ot.counter + ot.denominations!.length));

      // Bump cursor to at least the end of the manually allocated range
      await this._counterSource.advanceToAtLeast(keysetId, maxManualEnd);
      this._logger.debug('Counter source advanced to respect manual deterministic counters', {
        keysetId,
        maxManualEnd,
      });
    }

    // Reserve counters for deterministic outputTypes with auto counter (counter===0))
    const total = outputTypes.reduce((n, ot) => n + this.countersNeeded(ot), 0);
    if (total === 0) return { outputTypes };

    const range = await this.reserveFor(keysetId, total);
    let cursor = range.start;

    const patched = outputTypes.map((ot): OutputType => {
      if (ot.type === 'deterministic' && ot.counter === 0) {
        const need = ot.denominations?.length ?? 0;
        if (need > 0) {
          const patched: typeof ot = { ...ot, counter: cursor };
          cursor += need;
          return patched;
        }
      }
      return ot;
    });

    // Fire event after successful reservation (wallet does not await handlers)
    const used: OperationCounters = {
      keysetId,
      start: range.start,
      count: range.count,
      next: range.start + range.count,
    };
    this.on._emitCountersReserved(used);
    return { outputTypes: patched, used };
  }

  /**
   * Bind this wallet to a specific keyset id.
   *
   * @remarks
   * This changes the default keyset used by all operations that do not explicitly pass a keysetId.
   * The method validates that the keyset exists in the keychain, matches the wallet unit, and has
   * keys loaded.
   *
   * Typical uses:
   *
   * 1. After loadMint, to pin the wallet to a particular active keyset.
   * 2. After a refresh, to rebind deliberately rather than falling back to cheapest.
   *
   * @param id The keyset identifier to bind to.
   * @throws If keyset not found, if it has no keys loaded, or if its unit is not the wallet unit.
   */
  public bindKeyset(id: string): void {
    const ks = this._keyChain.getKeyset(id);
    this.failIf(ks.unit !== this._unit, 'Keyset unit does not match wallet unit', {
      keyset: ks.id,
      unit: ks.unit,
      walletUnit: this._unit,
    });
    this.failIf(!ks.hasKeys, 'Keyset has no keys loaded', { keyset: ks.id });
    this._boundKeysetId = ks.id;
    this._logger.debug('Wallet bound to keyset', {
      keysetId: ks.id,
      unit: ks.unit,
      feePPK: ks.fee,
    });
  }

  /**
   * Creates a new Wallet bound to a different keyset, sharing the same CounterSource.
   *
   * Use this to operate on multiple keysets concurrently without mutating your original wallet.
   * Counters remain monotonic across instances because the same CounterSource is reused.
   *
   * Do NOT pass a fresh CounterSource for the same seed unless you know exactly why. Reusing
   * counters can recreate secrets that a mint will reject.
   *
   * @param id The keyset identifier to bind to.
   * @throws If keyset not found, if it has no keys loaded, or if its unit is not the wallet unit.
   */
  public withKeyset(id: string, opts?: { counterSource?: CounterSource }): Wallet {
    const newWallet = new Wallet(this.mint, {
      keysetId: id,
      bip39seed: this._seed,
      secretsPolicy: this._secretsPolicy,
      outputDataCreator: this._outputDataCreator,
      requireSigDleq: this._requireSigDleq,
      logger: this._logger,
      counterSource: opts?.counterSource ?? this._counterSource,
    });
    // Load mint info from our caches
    newWallet.loadMintFromCache(this.getMintInfo().cache, this._keyChain.cache);
    return newWallet;
  }

  /**
   * Returns the default OutputType for this wallet, based on its configured secrets policy
   * (options?.secretsPolicy) and seed state.
   *
   * - If the secrets policy is 'random', returns { type: 'random' }.
   * - If the policy is 'deterministic', requires a seed and returns { type: 'deterministic', counter:
   *   0 }. Counter 0 is a flag meaning "auto-increment from current state".
   * - If no explicit policy is set, falls back to:
   *
   *   - Deterministic if a seed is present.
   *   - Random if no seed is present.
   *
   * @returns An OutputType object describing the default output strategy.
   * @throws Error if the policy is 'deterministic' but no seed has been set.
   */
  public defaultOutputType(): OutputType {
    if (this._secretsPolicy === 'random') return { type: 'random' };
    if (this._secretsPolicy === 'deterministic') {
      this.failIfNullish(this._seed, 'Deterministic policy requires a seed');
      return { type: 'deterministic', counter: 0 }; // 0 = auto flag
    }
    return this._seed ? { type: 'deterministic', counter: 0 } : { type: 'random' };
  }

  // -----------------------------------------------------------------
  // Section: Output Creation
  // -----------------------------------------------------------------

  /**
   * Configures output denominations with fee adjustments and optimization.
   *
   * @remarks
   * If 'custom' outputType, data outputs MUST sum to the amount. Other outputTypes may supply
   * denominations. If no denominations are passed in, they will be calculated based on proofsWeHave
   * or the default split. If partial denominations are passed in, the balance will be added using
   * default split. Additional denominations to cover fees will then be added if required.
   * @param amount The total amount for outputs.
   * @param keyset The mint keyset.
   * @param outputType The output configuration.
   * @param includeFees Whether to include swap fees in the output amount.
   * @param proofsWeHave Optional proofs for optimizing denomination splitting.
   * @returns OutputType with required denominations.
   */
  private configureOutputs(
    amount: AmountLike,
    keyset: Keyset,
    outputType: OutputType,
    includeFees: boolean = false,
    proofsWeHave: Array<Pick<ProofLike, 'amount'>> = [],
  ): OutputType {
    let newAmount = this.parseAmount(amount, 'configureOutputs', true);
    const normalizedProofsWeHave = proofsWeHave.map((p) => ({ amount: Amount.from(p.amount) }));

    // Custom outputs don't have automatic optimizations or fee inclusion)
    if (outputType.type === 'custom') {
      this.failIf(includeFees, 'The custom OutputType does not support automatic fee inclusion');

      // Validate sum early, as no denominations to fill
      const customTotal = this.parseAmount(
        OutputData.sumOutputAmounts(outputType.data),
        'configureOutputs.customTotal',
        true,
      );
      this.failIf(
        !customTotal.equals(newAmount),
        `Custom output data total (${customTotal.toString()}) does not match amount (${newAmount.toString()})`,
      );
      return outputType;
    }

    // Start with any denominations provided.
    // Note: These MAY be partial ("give me a [16,8], anything for the rest")
    // We will complete the denomination set before we are done.
    let denominations = outputType.denominations ?? [];

    // If no denominations, but proofsWeHave was provided - optimize
    // to get around _denominationTarget proofs of each denomination.
    if (denominations.length === 0 && normalizedProofsWeHave.length > 0) {
      denominations = getKeepAmounts(
        normalizedProofsWeHave,
        newAmount,
        keyset.keys,
        this._denominationTarget,
      );
    }

    // Fill in any missing denominations with default split.
    // NOTE: If we have to fill, the result will be in ASC order.
    // Original order is only maintained for exact denomination sets.
    denominations = splitAmount(newAmount, keyset.keys, denominations);

    // If includeFees, we create additional output amounts to cover the
    // fee the receiver will pay when they spend the proofs (ie sender pays fees)
    if (includeFees) {
      let receiveFee = this.getFeesForKeyset(denominations.length, keyset.id);
      let receiveFeeAmounts = splitAmount(receiveFee, keyset.keys);
      while (
        this.getFeesForKeyset(
          denominations.length + receiveFeeAmounts.length,
          keyset.id,
        ).greaterThan(receiveFee)
      ) {
        receiveFee = receiveFee.add(1);
        receiveFeeAmounts = splitAmount(receiveFee, keyset.keys);
      }
      newAmount = newAmount.add(receiveFee);
      denominations = [...denominations, ...receiveFeeAmounts];
    }
    return { ...outputType, denominations };
  }

  /**
   * Sum total implied by a prepared OutputType.
   *
   * Note: Empty denomination is valid (e.g: zero change).
   */
  private preparedTotal(ot: OutputType): Amount {
    if (ot.type === 'custom') return OutputData.sumOutputAmounts(ot.data);
    const denoms = ot.denominations ?? [];
    return Amount.sum(denoms);
  }

  /**
   * Generates blinded messages based on the specified output type.
   *
   * @param amount The total amount for outputs.
   * @param keyset The mint keys.
   * @param outputType The output configuration.
   * @returns Prepared output data.
   */
  private createOutputData(
    amount: AmountLike,
    keyset: Keyset,
    outputType: OutputType,
  ): OutputDataLike[] {
    const outputAmount = this.parseAmount(amount, 'createOutputData', true); // allow zero
    if (
      // 'custom' OutputType has no denominations. Every other OutputType does.
      // so let's sanity check those were filled properly (eg: configureOutputs)
      'custom' != outputType.type &&
      outputType.denominations &&
      outputType.denominations.length > 0
    ) {
      const splitSum = Amount.sum(outputType.denominations);
      this.failIf(
        !splitSum.equals(outputAmount),
        'Denominations do not sum to the expected amount',
        {
          splitSum: splitSum.toString(),
          expected: outputAmount.toString(),
        },
      );
    }
    let outputData: OutputDataLike[];
    switch (outputType.type) {
      case 'random':
        outputData = this._outputDataCreator.createRandomData(
          outputAmount,
          keyset,
          outputType.denominations,
        );
        break;
      case 'deterministic':
        this.failIfNullish(
          this._seed,
          'Deterministic outputs require a seed configured in the wallet',
        );
        outputData = this._outputDataCreator.createDeterministicData(
          outputAmount,
          this._seed,
          outputType.counter,
          keyset,
          outputType.denominations,
        );
        break;
      case 'p2pk':
        outputData = this._outputDataCreator.createP2PKData(
          outputType.options,
          outputAmount,
          keyset,
          outputType.denominations,
        );
        break;
      case 'factory': {
        const factorySplit = splitAmount(outputAmount, keyset.keys, outputType.denominations);
        outputData = factorySplit.map((a) => outputType.factory(a, keyset));
        break;
      }
      case 'custom': {
        outputData = outputType.data;
        const customTotal = this.parseAmount(
          OutputData.sumOutputAmounts(outputData),
          'createOutputData.customTotal',
          true,
        );
        this.failIf(
          !customTotal.equals(outputAmount),
          `Custom output data total (${customTotal.toString()}) does not match amount (${outputAmount.toString()})`,
        );

        break;
      }
      default: {
        this.fail('Invalid OutputType');
      }
    }
    return outputData;
  }

  /**
   * Creates a swap transaction with sorted outputs for privacy. This prevents a mint working out
   * which proofs will be sent or kept.
   *
   * @param inputs Prepared input proofs.
   * @param keepOutputs Outputs to keep (change or receiver's proofs).
   * @param sendOutputs Outputs to send (optional, default empty for receive/mint).
   * @returns Swap transaction with payload and metadata for processing signatures.
   */
  private createSwapTransaction(
    inputs: Proof[],
    keepOutputs: OutputDataLike[],
    sendOutputs: OutputDataLike[] = [],
  ): SwapTransaction {
    // Prepare inputs for mint
    inputs = this._prepareInputsForMint(inputs);

    // Sort ASC by amount for privacy, but keep indices to return order afterwards
    // But ONLY if the transaction is NOT SIG_ALL (as order is fixed for signing)
    const mergedBlindingData = [...keepOutputs, ...sendOutputs];
    const indices = mergedBlindingData.map((_, i) => i);
    if (!isP2PKSigAll(inputs)) {
      indices.sort((a, b) => {
        return mergedBlindingData[a].blindedMessage.amount.compareTo(
          mergedBlindingData[b].blindedMessage.amount,
        );
      });
    }
    const keepVector: boolean[] = [
      ...Array.from({ length: keepOutputs.length }, () => true),
      ...Array.from({ length: sendOutputs.length }, () => false),
    ];
    const sortedOutputData: OutputDataLike[] = indices.map((i) => mergedBlindingData[i]);
    const sortedKeepVector: boolean[] = indices.map((i) => keepVector[i]);
    const outputs = sortedOutputData.map((d) => d.blindedMessage);
    this._logger.debug('createSwapTransaction:', {
      indices,
      sortedKeepVector,
      // outputs, // <-- removed for security
    });
    const payload: SwapRequest = {
      inputs,
      outputs,
    };
    return {
      payload,
      outputData: sortedOutputData,
      keepVector: sortedKeepVector,
      sortedIndices: indices,
    };
  }

  // -----------------------------------------------------------------
  // Section: Send and Receive
  // -----------------------------------------------------------------

  /**
   * Receive a token (swaps with mint for new proofs)
   *
   * @example
   *
   * ```typescript
   * const result = await wallet.receive(
   *   token,
   *   { requireDleq: true },
   *   { type: 'deterministic', counter: 0 },
   * );
   * ```
   *
   * @param token Token string, decoded token, or raw proof array.
   * @param config Optional receive config.
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns Newly minted proofs.
   */
  async receive(
    token: Token | string | ProofLike[],
    config?: ReceiveConfig,
    outputType?: OutputType,
  ): Promise<Proof[]> {
    // Prepare and complete the send
    const txn = await this.prepareSwapToReceive(token, config, outputType);
    const { keep } = await this.completeSwap(txn, config?.privkey);
    return keep;
  }

  /**
   * Prepare A Receive Transaction.
   *
   * @remarks
   * Allows you to preview fees for a receive, get concrete outputs for P2PK SIG_ALL transactions,
   * and do any pre-swap tasks (such as marking proofs in-flight etc)
   * @example
   *
   * ```typescript
   * // Prepare transaction
   * const txn = await wallet.prepareSwapToReceive(token, { requireDleq: true });
   * const fees = txn.fees;
   *
   * // Complete transaction
   * const { keep } = await wallet.completeSwap(txn);
   * ```
   *
   * @param token Token string, decoded token, or raw proof array.
   * @param config Optional receive config.
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns SwapPreview with metadata for swap transaction.
   */
  async prepareSwapToReceive(
    token: Token | string | ProofLike[],
    config?: ReceiveConfig,
    outputType?: OutputType,
  ): Promise<SwapPreview> {
    const { keysetId, requireDleq, proofsWeHave, onCountersReserved } = config || {};
    outputType = outputType ?? this.defaultOutputType(); // Fallback to policy

    // Extract proofs — either directly or by decoding the token
    let proofs: Proof[];
    if (Array.isArray(token)) {
      proofs = normalizeProofAmounts(token);
    } else {
      const decodedToken: Token = typeof token === 'string' ? this.decodeToken(token) : token;
      const tokenMintUrl = normalizeUrl(decodedToken.mint);
      this.failIf(tokenMintUrl !== this.mint.mintUrl, 'Token belongs to a different mint', {
        token: tokenMintUrl,
        wallet: this.mint.mintUrl,
      });
      this.failIf(decodedToken.unit !== this._unit, 'Token is not in wallet unit', {
        token: decodedToken.unit,
        wallet: this._unit,
      });
      // Token object may come from JSON.parse/localStorage and need runtime rehydration.
      proofs = normalizeProofAmounts(decodedToken.proofs);
    }

    // Validate all proof keyset IDs use this wallet's unit
    const knownIds = this._keyChain.getKeysets().map((k) => k.id);
    const badProof = proofs.find((p) => !p.id || !knownIds.includes(p.id));
    this.failIf(
      !!badProof,
      `Proof has unrecognised keyset. '${badProof?.id}' is not a ${this._unit} keyset from this mint`,
      {
        id: badProof?.id,
        knownIds,
      },
    );

    // Check total amount
    const totalAmount = this.parseAmount(sumProofs(proofs), 'prepareSwapToReceive', true);
    this.failIf(totalAmount.isZero(), 'Token contains no proofs', { proofs });

    // NUT-12: wallets MUST verify any DLEQ on a received proof. `requireDleq: true`
    // upgrades that to "DLEQ must also be present" via the stricter `hasValidDleq`.
    for (const p of proofs) {
      const ks = this._keyChain.getKeyset(p.id);
      if (requireDleq) {
        this.failIf(!hasValidDleq(p, ks), 'Token contains proofs with invalid or missing DLEQ');
      } else {
        this.failIf(!verifyDleqIfPresent(p, ks), 'Token contains a proof with an invalid DLEQ');
      }
    }

    // Shape receive output type and denominations
    const keyset = this.getOutputKeyset(keysetId); // specified or wallet keyset
    const swapFee = this.getFeesForProofs(proofs);
    const receiveAmount = totalAmount.subtract(swapFee);
    let receiveOT = this.configureOutputs(
      receiveAmount,
      keyset,
      outputType,
      false, // includeFees is not applicable for receive
      proofsWeHave,
    );

    // Assign counter atomically if OutputType is deterministic
    // and the counter is zero (auto-assign)
    const autoCounters = await this.addCountersToOutputTypes(keyset.id, receiveOT);
    [receiveOT] = autoCounters.outputTypes;
    if (autoCounters.used) {
      this.safeCallback(onCountersReserved, autoCounters.used, { op: 'receive' });
    }
    this._logger.debug('receive counter', {
      counter: autoCounters.used,
      receiveOT: stringifyOutputTypeForLog(receiveOT),
    });

    // Create outputs and execute swap
    const outputs = this.createOutputData(this.preparedTotal(receiveOT), keyset, receiveOT);

    // Return SwapPreview
    return {
      amount: receiveAmount,
      fees: swapFee,
      keysetId: keyset.id,
      inputs: proofs,
      keepOutputs: outputs,
    };
  }

  /**
   * Sends proofs of a given amount from provided proofs.
   *
   * @remarks
   * If proofs are P2PK-locked to your public key, call signP2PKProofs first to sign them. The
   * default config uses exact match selection, and does not includeFees or requireDleq. Because the
   * send is offline, the user will unlock the signed proofs when they receive them online.
   * @param amount Amount to send.
   * @param proofs Array of proofs (must sum >= amount; pre-sign if P2PK-locked).
   * @param config Optional parameters for the send.
   * @returns SendResponse with keep/send proofs.
   * @throws Throws if the send cannot be completed offline.
   */
  sendOffline(amount: AmountLike, proofs: ProofLike[], config?: SendOfflineConfig): SendResponse {
    const sendAmount = this.parseAmount(amount, 'sendOffline');
    let normalizedProofs = normalizeProofAmounts(proofs);
    const { requireDleq = false, includeFees = false, exactMatch = true } = config || {};
    if (requireDleq) {
      // Only use proofs that have a DLEQ
      normalizedProofs = normalizedProofs.filter((p) => p.dleq != undefined);
    }
    this.failIf(
      sumProofs(normalizedProofs).lessThan(sendAmount),
      'Not enough funds available to send',
    );

    const { keep, send } = this.selectProofsToSend(
      normalizedProofs,
      sendAmount,
      includeFees,
      exactMatch,
    );
    // Ensure witnesses are serialized, strip DLEQ if not required, keep p2pk_e
    const sendPrepared = this._prepareInputsForMint(send, requireDleq, true);
    return { keep, send: sendPrepared };
  }

  /**
   * Send proofs with online swap if necessary.
   *
   * @example
   *
   * ```typescript
   * // Simple send
   * const result = await wallet.send(5, proofs);
   *
   * // With a SendConfig
   * const result = await wallet.send(5, proofs, { includeFees: true });
   *
   * // With Custom output configuration
   * const customConfig: OutputConfig = {
   *   send: { type: 'p2pk', options: { pubkey: '...' } },
   *   keep: { type: 'deterministic', counter: 0 },
   * };
   * const customResult = await wallet.send(5, proofs, { includeFees: true }, customConfig);
   * ```
   *
   * @param amount Amount to send (receiver gets this net amount).
   * @param proofs Array of proofs to split.
   * @param config Optional parameters for the swap.
   * @returns SendResponse with keep/send proofs.
   * @throws Throws if the send cannot be completed offline or if funds are insufficient.
   */
  async send(
    amount: AmountLike,
    proofs: ProofLike[],
    config?: SendConfig,
    outputConfig?: OutputConfig,
  ): Promise<SendResponse> {
    const sendAmount = this.parseAmount(amount, 'send');
    const { keysetId, includeFees = false } = config || {};
    // Fallback to policy defaults if no outputConfig
    outputConfig = outputConfig ?? {
      send: this.defaultOutputType(),
      keep: this.defaultOutputType(),
    };

    // First, let's see if we can avoid a swap (and fees)
    // by trying an exact match offline selection, including fees if
    // we are giving the receiver the amount + their fee to receive
    try {
      // Offline exact-match only allowed for plain-random defaults; deterministic implies swap.
      const wantsDeterministicByPolicy = this.defaultOutputType().type === 'deterministic';
      const isPlainRandom = (ot?: OutputType) =>
        !ot || (ot.type === 'random' && (!ot.denominations || ot.denominations.length === 0));

      if (
        keysetId ||
        wantsDeterministicByPolicy ||
        !isPlainRandom(outputConfig.send) ||
        (outputConfig.keep && !isPlainRandom(outputConfig.keep))
      ) {
        // Explain why we must fall back to swap
        const reasons: string[] = [];
        if (keysetId) reasons.push('keysetId override');
        if (wantsDeterministicByPolicy) reasons.push('wallet default is deterministic');
        if (!isPlainRandom(outputConfig.send)) reasons.push('non-default send output type');
        if (outputConfig.keep && !isPlainRandom(outputConfig.keep))
          reasons.push('non-default keep output type');

        throw new CTSError(`Options require a swap: ${reasons.join(', ')}`);
      }

      // Proceed with offline exact-match attempt
      const { keep, send } = this.sendOffline(sendAmount, proofs, {
        includeFees,
        exactMatch: true,
        requireDleq: false, // safety
      });
      const expectedFee = includeFees ? this.getFeesForProofs(send) : Amount.zero();

      if (sumProofs(send).equals(sendAmount.add(expectedFee))) {
        this._logger.info('Successful exactMatch offline selection!');
        return { keep, send };
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      this._logger.debug('ExactMatch offline selection failed.', { e: message });
    }

    // Prepare and complete the send
    const txn = await this.prepareSwapToSend(sendAmount, proofs, config, outputConfig);
    return await this.completeSwap(txn, config?.privkey);
  }

  /**
   * Prepare A Send Transaction.
   *
   * @remarks
   * Allows you to preview fees for a send, get concrete outputs for P2PK SIG_ALL transactions, and
   * do any pre-swap tasks (such as marking proofs in-flight etc)
   * @example
   *
   * ```typescript
   * // Prepare transaction
   * const txn = await wallet.prepareSwapToSend(5, proofs, { includeFees: true });
   * const fees = txn.fees;
   *
   * // Complete transaction
   * const { keep, send } = await wallet.completeSwap(txn);
   * ```
   *
   * @param amount Amount to send (receiver gets this net amount).
   * @param proofs Array of proofs to split.
   * @param config Optional parameters for the swap.
   * @returns SwapPreview with metadata for swap transaction.
   * @throws Throws if the send cannot be completed offline or if funds are insufficient.
   */
  async prepareSwapToSend(
    amount: AmountLike,
    proofs: ProofLike[],
    config?: SendConfig,
    outputConfig?: OutputConfig,
  ): Promise<SwapPreview> {
    const sendAmountTarget = this.parseAmount(amount, 'prepareSwapToSend');
    const normalizedProofs = normalizeProofAmounts(proofs);
    const { keysetId, includeFees = false, onCountersReserved } = config || {};

    // Fallback to policy defaults if no outputConfig
    outputConfig = outputConfig ?? {
      send: this.defaultOutputType(),
      keep: this.defaultOutputType(),
    };

    // Fetch keys
    const keyset = this.getOutputKeyset(keysetId); // specified or wallet keyset

    // Shape SEND output type and denominations
    let sendOT = this.configureOutputs(
      sendAmountTarget,
      keyset,
      outputConfig.send ?? this.defaultOutputType(),
      includeFees,
    );
    const sendAmount = this.preparedTotal(sendOT);

    // Select the subset of proofs needed to cover the swap (sendTarget + swap fee)
    const { keep: unselectedProofs, send: selectedProofs } = this.selectProofsToSend(
      normalizedProofs,
      sendAmount,
      true, // Include fees to cover swap fee
    );
    // this._logger.debug('PROOFS SELECTED', {
    // 	unselectedProofs: unselectedProofs.map(p=>p.amount.toString()),
    // 	selectedProofs: selectedProofs.map(p=>p.amount.toString()),
    // });
    if (selectedProofs.length === 0) {
      throw new CTSError('Not enough funds available to send');
    }

    // Calculate our expected change from the swap (and sanity check!)
    const selectedSum = sumProofs(selectedProofs);
    const swapFee = this.getFeesForProofs(selectedProofs);
    const required = sendAmount.add(swapFee);
    if (selectedSum.lessThan(required)) {
      this.failIf(true, 'Not enough funds available for swap', {
        selectedSum: selectedSum.toString(),
        required: required.toString(),
      });
    }
    const changeAmount = selectedSum.subtract(required);

    // Shape KEEP (change) output type and denominations
    // No includeFees, as we are the receiver of the change
    let keepOT = this.configureOutputs(
      changeAmount,
      keyset,
      outputConfig.keep ?? this.defaultOutputType(),
      false,
      config?.proofsWeHave,
    );
    const keepAmount = this.preparedTotal(keepOT);

    // Assign counters atomically if either/both OutputTypes are deterministic
    // and the counter is zero (auto-assign)
    const autoCounters = await this.addCountersToOutputTypes(keyset.id, sendOT, keepOT);
    [sendOT, keepOT] = autoCounters.outputTypes;
    if (autoCounters.used) {
      this.safeCallback(onCountersReserved, autoCounters.used, { op: 'send' });
    }
    this._logger.debug('send counters', {
      counter: autoCounters.used,
      sendOT: stringifyOutputTypeForLog(sendOT),
      keepOT: stringifyOutputTypeForLog(keepOT),
    });

    // Create the output data
    const sendOutputs = this.createOutputData(sendAmount, keyset, sendOT);
    const keepOutputs = this.createOutputData(keepAmount, keyset, keepOT);

    // Return SwapPreview
    return {
      amount: sendAmountTarget,
      fees: swapFee,
      keysetId: keyset.id,
      inputs: selectedProofs,
      sendOutputs,
      keepOutputs,
      unselectedProofs,
    };
  }

  /**
   * Complete a prepared swap transaction.
   *
   * @example
   *
   * ```typescript
   * // Prepare transaction
   * const txn = await wallet.prepareSwapToSend(5, proofs, { includeFees: true });
   *
   * // Complete transaction
   * const result = await wallet.completeSwap(txn);
   * ```
   *
   * @param swapPreview With metadata for swap transaction.
   * @param privkey The private key(s) for signing.
   * @returns SendResponse with keep/send proofs.
   */
  async completeSwap(swapPreview: SwapPreview, privkey?: string | string[]): Promise<SendResponse> {
    const keepOutputs: OutputDataLike[] = swapPreview?.keepOutputs ? swapPreview.keepOutputs : [];
    const sendOutputs: OutputDataLike[] = swapPreview.sendOutputs ? swapPreview.sendOutputs : [];
    const unselectedProofs: Proof[] = swapPreview.unselectedProofs
      ? swapPreview.unselectedProofs
      : [];

    // Sign proofs if needed
    if (privkey) {
      swapPreview.inputs = this.signP2PKProofs(swapPreview.inputs, privkey, [
        ...keepOutputs,
        ...sendOutputs,
      ]);
    }

    // Create swap transaction
    const swapTransaction = this.createSwapTransaction(
      swapPreview.inputs,
      keepOutputs,
      sendOutputs,
    );

    // Execute swap and validate result
    const { signatures } = await this.mint.swap(swapTransaction.payload);
    this.failIf(
      signatures.length !== swapTransaction.outputData.length,
      `Mint returned ${signatures.length} signatures, expected ${swapTransaction.outputData.length}. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
    );
    this.validateReturnedSignatures(signatures, swapTransaction.outputData);

    // Construct proofs
    const keyset = this.getKeyset(swapPreview.keysetId);
    const swapProofs = swapTransaction.outputData.map((d, i) => d.toProof(signatures[i], keyset));
    const reorderedProofs = Array(swapProofs.length);
    const reorderedKeepVector = Array(swapTransaction.keepVector.length);
    swapTransaction.sortedIndices.forEach((s, i) => {
      reorderedKeepVector[s] = swapTransaction.keepVector[i];
      reorderedProofs[s] = swapProofs[i];
    });
    const keepProofs: Proof[] = [];
    const sendProofs: Proof[] = [];
    reorderedProofs.forEach((p: Proof, i) => {
      if (reorderedKeepVector[i]) {
        keepProofs.push(p);
      } else {
        sendProofs.push(p);
      }
    });
    this._logger.debug('SEND COMPLETED', {
      unselectedProofs: unselectedProofs.map((p) => p.amount.toString()),
      keepProofs: keepProofs.map((p) => p.amount.toString()),
      sendProofs: sendProofs.map((p) => p.amount.toString()),
    });
    return {
      keep: [...keepProofs, ...unselectedProofs],
      send: sendProofs,
    };
  }

  // -----------------------------------------------------------------
  // Section: Transaction Helpers
  // -----------------------------------------------------------------

  /**
   * Selects proofs to send based on amount and fee inclusion.
   *
   * @remarks
   * Uses an adapted Randomized Greedy with Local Improvement (RGLI) algorithm, which has a time
   * complexity O(n log n) and space complexity O(n).
   * @param proofs Array of proofs available to select from. Accepts {@link ProofLike} so proofs
   *   loaded from storage (with `amount: number`) work without manual conversion.
   * @param amountToSend The target amount to send.
   * @param includeFees Optional boolean to include fees; Default: false.
   * @param exactMatch Optional boolean to require exact match; Default: false.
   * @returns SendResponse containing proofs to keep and proofs to send.
   * @throws Throws an error if an exact match cannot be found within MAX_TIMEMS.
   * @see https://crypto.ethz.ch/publications/files/Przyda02.pdf
   */
  selectProofsToSend(
    proofs: ProofLike[],
    amountToSend: AmountLike,
    includeFees = false,
    exactMatch = false,
  ): SendResponse {
    const parsedAmountToSend = this.parseAmount(amountToSend, 'selectProofsToSend');
    const { keep, send } = this._selectProofs(
      proofs,
      parsedAmountToSend,
      this._keyChain,
      includeFees,
      exactMatch,
    );
    return { keep, send };
  }

  /**
   * Prepares proofs for sending by signing P2PK-locked proofs.
   *
   * @remarks
   * Call this method before operations like send if the proofs are P2PK-locked and need unlocking.
   * This is a public wrapper for signing.
   * @param proofs The proofs to sign.
   * @param privkey The private key(s) for signing.
   * @param outputData Optional. For signing of SIG_ALL transactions.
   * @param quoteId Optional. For signing SIG_ALL melt transactions.
   * @returns Signed proofs.
   */
  signP2PKProofs(
    proofs: ProofLike[],
    privkey: string | string[],
    outputData?: OutputDataLike[],
    quoteId?: string,
  ): Proof[] {
    const normalizedProofs = normalizeProofAmounts(proofs);
    // Normal case, sign everything as usual
    if (!isP2PKSigAll(normalizedProofs)) {
      return cryptoSignP2PKProofs(normalizedProofs, privkey, this._logger);
    }

    // Ensure SIG_ALL conditions are met
    this.failIfNullish(outputData, 'OutputData is required for SIG_ALL proof signing.');
    assertSigAllInputs(normalizedProofs);

    // SIG_ALL is in flux currently, so let's generate all known message formats
    // and sign the first proof only against each message...
    const [first, ...rest] = normalizedProofs;
    let signedFirst = first;
    const messages = [
      buildLegacyP2PKSigAllMessage(normalizedProofs, outputData, quoteId),
      buildP2PKSigAllMessage(normalizedProofs, outputData, quoteId),
    ];
    for (const msg of messages) {
      signedFirst = cryptoSignP2PKProofs([signedFirst], privkey, this._logger, msg)[0];
    }

    // Return the proofs in same order as before
    return [signedFirst, ...rest];
  }

  /**
   * Calculates the fees based on inputs (proofs)
   *
   * @param proofs Input proofs to calculate fees for.
   * @returns Fee amount.
   * @throws Throws an error if the proofs keyset is unknown.
   */
  getFeesForProofs(proofs: Array<Pick<Proof, 'id'>>): Amount {
    const sumPPK = Amount.sum(proofs.map((proof) => this.getProofFeePPK(proof))).toBigInt();
    return Amount.from((sumPPK + 999n) / 1000n);
  }

  /**
   * Returns the current fee PPK for a proof according to the cached keyset.
   *
   * @param proof {Proof} A single proof.
   * @returns FeePPK {number} The feePPK for the selected proof.
   * @throws Throws an error if the proofs keyset is unknown.
   */
  private getProofFeePPK(proof: Pick<Proof, 'id'>): number {
    try {
      // We need the proof's keyset so use keyChain here
      // We must NOT fallback to wallet's keyset
      return this._keyChain.getKeyset(proof.id).fee;
    } catch (e) {
      const message = `Could not get fee. No keyset found for keyset id: ${proof.id}`;
      this._logger.error(message, {
        e,
        keychain: this._keyChain.getKeysets(),
      });
      throw new CTSError(message, { cause: e });
    }
  }

  /**
   * Calculates the fees based on inputs for a given keyset.
   *
   * @param nInputs Number of inputs.
   * @param keysetId KeysetId used to lookup `input_fee_ppk`
   * @returns Fee amount.
   */
  getFeesForKeyset(nInputs: number, keysetId: string): Amount {
    try {
      // We must NOT fallback to wallet's keyset
      const feePPK = this._keyChain.getKeyset(keysetId).fee;
      return Amount.from(Math.floor(Math.max((nInputs * feePPK + 999) / 1000, 0)));
    } catch (e) {
      const message = `No keyset found with ID ${keysetId}`;
      this._logger.error(message, { e });
      throw new CTSError(message, { cause: e });
    }
  }

  /**
   * Largest spendable amount from a proof set after subtracting the per-proof input fees the mint
   * charges and optionally a melt quote's `fee_reserve`.
   *
   * @remarks
   * Single step of a "melt all" iteration: fetch a melt quote, call this with the quote's
   * `fee_reserve` to get the largest amount that fits, then re-quote for that amount (since
   * `fee_reserve` may shrink with the smaller amount). Repeat until the result stabilises.
   * @param proofs Proofs the caller intends to spend.
   * @param feeReserve Optional. `fee_reserve` from a related melt quote (default: 0)
   * @returns The largest spendable amount, or zero if fees exceed the available total.
   */
  maxSpendableAfterFees(proofs: ProofLike[], feeReserve: AmountLike = 0): Amount {
    const total = sumProofs(proofs);
    const overhead = this.getFeesForProofs(proofs).add(feeReserve);
    return overhead.greaterThanOrEqual(total) ? Amount.zero() : total.subtract(overhead);
  }

  /**
   * Prepares inputs for a mint operation.
   *
   * @remarks
   * Internal method; strips DLEQ (NUT-12) and p2pk_e (NUT-28) for privacy and serializes witnesses.
   * Returns an array of new proof objects - does not mutate the originals.
   * @param proofs The proofs to prepare.
   * @param keepDleq Optional boolean to keep DLEQ (default: false, strips for privacy).
   * @param keepP2pkE Optional boolean to keep NUT-28 "E" (default: false, strips for privacy).
   * @returns Prepared proofs for mint payload.
   */
  private _prepareInputsForMint(
    proofs: Proof[],
    keepDleq: boolean = false,
    keepP2pkE: boolean = false,
  ): Proof[] {
    return proofs.map((p) => {
      const witness = this._normalizeWitness(p);
      const { dleq, p2pk_e, ...rest } = p; // isolate dleq and p2pk_e
      let newProof: Proof = { ...rest, witness }; // add back normalized witness
      if (keepP2pkE && p2pk_e) newProof = { ...newProof, p2pk_e };
      if (keepDleq && dleq) newProof = { ...newProof, dleq };
      return newProof;
    });
  }

  /**
   * Normalizes a proof's witness for the mint payload.
   *
   * Serializes object witnesses to JSON and strips witnesses from non-NUT-10 secrets (plain secrets
   * with a witness field are rejected by mints).
   */
  private _normalizeWitness(proof: Proof): string | undefined {
    if (!proof.witness) return undefined;
    try {
      parseSecret(proof.secret);
    } catch {
      return undefined; // plain secret — strip stray witness
    }
    return typeof proof.witness !== 'string' ? JSON.stringify(proof.witness) : proof.witness;
  }

  /**
   * Decodes a string token.
   *
   * @remarks
   * Rehydrates a token from the space-saving CBOR format, including mapping short keyset ids to
   * their full representation.
   * @param token The token in string format (cashuB...)
   * @returns Token object.
   */
  public decodeToken(token: string): Token {
    const keysetIds = this._keyChain.getAllKeysetIds();
    return getDecodedToken(token, keysetIds);
  }

  // -----------------------------------------------------------------
  // Section: Restore
  // -----------------------------------------------------------------

  /**
   * Restores batches of deterministic proofs until no more signatures are returned from the mint.
   *
   * @param [gapLimit=300] The amount of empty counters that should be returned before restoring
   *   ends (defaults to 300). Default is `300`
   * @param [batchSize=300] The amount of proofs that should be restored at a time (defaults to
   *   300). Default is `300`
   * @param [counter=0] The counter that should be used as a starting point (defaults to 0). Default
   *   is `0`
   * @param [keysetId] Which keysetId to use for the restoration. If none is passed the instance's
   *   default one will be used.
   */
  async batchRestore(
    gapLimit = 300,
    batchSize = 300,
    counter = 0,
    keysetId?: string,
  ): Promise<{ proofs: Proof[]; lastCounterWithSignature?: number }> {
    const requiredEmptyBatches = Math.ceil(gapLimit / batchSize);
    const restoredProofs: Proof[] = [];

    let lastCounterWithSignature: undefined | number;
    let emptyBatchesFound = 0;

    while (emptyBatchesFound < requiredEmptyBatches) {
      const restoreRes = await this.restore(counter, batchSize, { keysetId });
      if (restoreRes.proofs.length > 0) {
        emptyBatchesFound = 0;
        restoredProofs.push(...restoreRes.proofs);
        lastCounterWithSignature = restoreRes.lastCounterWithSignature;
      } else {
        emptyBatchesFound++;
      }
      counter += batchSize;
    }
    return { proofs: restoredProofs, lastCounterWithSignature };
  }

  /**
   * Regenerates.
   *
   * @param start Set starting point for count (first cycle for each keyset should usually be 0)
   * @param count Set number of blinded messages that should be generated.
   * @param options.keysetId Set a custom keysetId to restore from. @see `keyChain)`
   */
  async restore(
    start: number,
    count: number,
    config?: RestoreConfig,
  ): Promise<{ proofs: Proof[]; lastCounterWithSignature?: number }> {
    this.failIfNullish(this._seed, 'Cashu Wallet must be initialized with a seed to use restore');
    const { keysetId } = config || {};

    // Ensure we have keys - wallet only loads active keysets by default
    await this._keyChain.ensureKeysetKeys(keysetId ?? this.keysetId);
    const keyset = this.getKeyset(keysetId); // specified or wallet keyset

    // create deterministic blank outputs for unknown restore amounts
    // Note: zero amount + zero denomination passes splitAmount validation
    const zeros = Array(count).fill(0);
    const outputData = this._outputDataCreator.createDeterministicData(
      0,
      this._seed,
      start,
      keyset,
      zeros,
    );

    const { outputs, signatures } = await this.mint.restore({
      outputs: outputData.map((d) => d.blindedMessage),
    });

    const signatureMap: { [sig: string]: SerializedBlindedSignature } = {};
    outputs.forEach((o, i) => (signatureMap[o.B_] = signatures[i]));

    const restoredProofs: Proof[] = [];
    let lastCounterWithSignature: number | undefined;

    for (let i = 0; i < outputData.length; i++) {
      const matchingSig = signatureMap[outputData[i].blindedMessage.B_];
      if (matchingSig) {
        lastCounterWithSignature = start + i;
        outputData[i].blindedMessage.amount = matchingSig.amount;
        restoredProofs.push(outputData[i].toProof(matchingSig, keyset));
      }
    }

    return {
      proofs: restoredProofs,
      lastCounterWithSignature,
    };
  }

  // -----------------------------------------------------------------
  // Section: Create Mint Quote
  // -----------------------------------------------------------------

  /**
   * Creates a mint quote for any payment method.
   *
   * @remarks
   * Generic method for requesting a mint quote. The payload is method-specific but must at minimum
   * include the fields required by the mint for the given method. An optional `normalize` callback
   * can be used to coerce method-specific response fields.
   *
   * For first-class methods, prefer the typed helpers: `createMintQuoteBolt11()`,
   * `createMintQuoteBolt12()`.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param payload The request body to POST. `unit` is always set to the wallet's unit.
   * @param options.normalize Optional callback to normalize method-specific response fields.
   * @returns The mint quote response.
   */
  async createMintQuote<TRes extends MintQuoteBaseResponse = MintQuoteBaseResponse>(
    method: string,
    payload: Record<string, unknown>,
    options?: { normalize?: (raw: Record<string, unknown>) => TRes },
  ): Promise<TRes> {
    // Kept permissive for v4 compatibility; this becomes an error in v5.
    // (Direct keychain probe rather than requireMintableKeyset, which logs an error.)
    try {
      this._keyChain.getCheapestKeyset();
    } catch (e) {
      this._logger.warn(
        `createMintQuote: no active keyset for unit '${this._unit}' — a paid mint quote could not be redeemed. This will become an error in cashu-ts v5.`,
        { reason: (e as Error).message },
      );
    }
    const body = { ...payload, unit: this._unit };
    const res = await this.mint.createMintQuote<TRes>(method, body, {
      normalize: options?.normalize,
    });
    return { ...res, unit: res.unit || this._unit };
  }

  /**
   * Requests a mint quote from the mint. Response returns a Lightning payment request for the
   * requested given amount and unit.
   *
   * @param amount Amount requesting for mint.
   * @param description Optional description for the mint quote.
   * @param pubkey Optional public key to lock the quote to.
   * @returns The mint will return a mint quote with a Lightning invoice for minting tokens of the
   *   specified amount and unit.
   */
  async createMintQuoteBolt11(
    amount: AmountLike,
    description?: string,
  ): Promise<MintQuoteBolt11Response> {
    this.requireSupport('mint', 'bolt11');
    this.requireMintableKeyset('createMintQuoteBolt11');
    const mintAmount = this.parseAmount(amount, 'createMintQuoteBolt11');
    // Check if mint supports description for bolt11
    if (description) {
      const mintInfo = this.getMintInfo();
      if (!mintInfo.supportsNut04Description('bolt11', this._unit)) {
        this.fail('Mint does not support description for bolt11');
      }
    }

    const mintQuotePayload: MintQuoteBolt11Request = {
      unit: this._unit,
      amount: mintAmount,
      description: description,
    };
    const res = await this.mint.createMintQuoteBolt11(mintQuotePayload);
    return { ...res, unit: res.unit || this._unit };
  }

  /**
   * Requests a mint quote from the mint that is locked to a public key.
   *
   * @param amount Amount requesting for mint.
   * @param pubkey Public key to lock the quote to.
   * @param description Optional description for the mint quote.
   * @returns The mint will return a mint quote with a Lightning invoice for minting tokens of the
   *   specified amount and unit. The quote will be locked to the specified `pubkey`.
   */
  async createLockedMintQuote(
    amount: AmountLike,
    pubkey: string,
    description?: string,
  ): Promise<MintQuoteBolt11Response> {
    this.requireSupport('mint', 'bolt11');
    this.requireMintableKeyset('createLockedMintQuote');
    const mintAmount = this.parseAmount(amount, 'createLockedMintQuote');
    const { supported } = this.getMintInfo().isSupported(20);
    this.failIf(!supported, 'Mint does not support NUT-20');
    const mintQuotePayload: MintQuoteBolt11Request = {
      unit: this._unit,
      amount: mintAmount,
      description: description,
      pubkey: pubkey,
    };
    const res = await this.mint.createMintQuoteBolt11(mintQuotePayload);
    this.failIf(typeof res.pubkey !== 'string', 'Mint returned unlocked mint quote');
    const resPubkey = res.pubkey!;
    return { ...res, pubkey: resPubkey, unit: res.unit || this._unit };
  }

  /**
   * Requests a mint quote from the mint. Response returns a Lightning BOLT12 offer for the
   * requested given amount and unit.
   *
   * @param pubkey Public key to lock the quote to.
   * @param options.amount BOLT12 offer amount requesting for mint. If not specified, the offer will
   *   be amountless.
   * @param options.description Description for the mint quote.
   * @returns The mint will return a mint quote with a BOLT12 offer for minting tokens of the
   *   specified amount and unit.
   */
  async createMintQuoteBolt12(
    pubkey: string,
    options?: {
      amount?: AmountLike;
      description?: string;
    },
  ): Promise<MintQuoteBolt12Response> {
    this.requireSupport('mint', 'bolt12');
    this.requireMintableKeyset('createMintQuoteBolt12');
    // Check if mint supports description for bolt12
    const mintInfo = this.getMintInfo();
    if (options?.description && !mintInfo.supportsNut04Description('bolt12', this._unit)) {
      this.fail('Mint does not support description for bolt12');
    }

    const amount =
      options?.amount !== undefined
        ? this.parseAmount(options.amount, 'createMintQuoteBolt12')
        : undefined;

    const mintQuotePayload: MintQuoteBolt12Request = {
      pubkey: pubkey,
      unit: this._unit,
      amount,
      description: options?.description,
    };

    return this.mint.createMintQuoteBolt12(mintQuotePayload);
  }

  /**
   * Requests an onchain mint quote from the mint. Response returns a Bitcoin address for the
   * requested unit.
   *
   * @param pubkey Public key to lock the quote to. Required for onchain minting.
   * @returns The mint will return a mint quote with a Bitcoin address for minting tokens.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async createMintQuoteOnchain(pubkey: string): Promise<MintQuoteOnchainResponse> {
    this.requireSupport('mint', 'onchain');
    this.requireMintableKeyset('createMintQuoteOnchain');
    const res = await this.mint.createMintQuoteOnchain({ unit: this._unit, pubkey });
    return { ...res, unit: res.unit || this._unit };
  }

  // -----------------------------------------------------------------
  // Section: Check Mint Quote
  // -----------------------------------------------------------------

  /**
   * Checks an existing mint quote for any payment method.
   *
   * @remarks
   * Generic method for checking a mint quote status. An optional `normalize` callback can be used
   * to coerce method-specific response fields.
   *
   * For first-class methods, prefer the typed helpers: `checkMintQuoteBolt11()`,
   * `checkMintQuoteBolt12()`.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param quote Quote ID or quote object (must have a `quote` field).
   * @param options.normalize Optional callback to normalize method-specific response fields.
   * @returns The mint quote response.
   */
  async checkMintQuote<TRes extends MintQuoteBaseResponse = MintQuoteBaseResponse>(
    method: string,
    quote: string | Pick<TRes, 'quote'>,
    options?: { normalize?: (raw: Record<string, unknown>) => TRes },
  ): Promise<TRes> {
    const quoteId = typeof quote === 'string' ? quote : (quote as { quote: string }).quote;
    return this.mint.checkMintQuote<TRes>(method, quoteId, {
      normalize: options?.normalize,
    });
  }

  /**
   * Gets an existing mint quote from the mint.
   *
   * @param quote Quote ID.
   * @returns The mint will create and return a Lightning invoice for the specified amount.
   */
  async checkMintQuoteBolt11(
    quote: string | MintQuoteBolt11Response,
  ): Promise<MintQuoteBolt11Response> {
    const quoteId = typeof quote === 'string' ? quote : quote.quote;
    return this.mint.checkMintQuoteBolt11(quoteId);
  }

  /**
   * Gets an existing BOLT12 mint quote from the mint.
   *
   * @param quote Quote ID.
   * @returns The latest mint quote for the given quote ID.
   */
  async checkMintQuoteBolt12(quote: string): Promise<MintQuoteBolt12Response> {
    return this.mint.checkMintQuoteBolt12(quote);
  }

  /**
   * Gets an existing onchain mint quote from the mint.
   *
   * @param quote Quote ID.
   * @returns The latest mint quote for the given quote ID.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async checkMintQuoteOnchain(quote: string): Promise<MintQuoteOnchainResponse> {
    return this.mint.checkMintQuoteOnchain(quote);
  }

  // -----------------------------------------------------------------
  // Section: Mint Proofs
  // -----------------------------------------------------------------

  private validateReturnedSignatures(
    signatures: SerializedBlindedSignature[],
    outputData: OutputDataLike[],
    options?: { checkAmounts?: boolean },
  ): void {
    // With DLEQ we can verify the returned blinded signature is paired to the original B_.
    // Without DLEQ, equal-denomination reordering is a protocol trust assumption.
    const requiresDleq =
      this._requireSigDleq && (this._mintInfo?.isSupported(12).supported ?? false);
    const checkAmounts = options?.checkAmounts ?? true;

    for (let i = 0; i < signatures.length; i++) {
      this.failIf(
        signatures[i] == undefined,
        `Mint response is missing a signature at index ${i}. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
      );
      this.failIf(
        signatures[i].id !== outputData[i].blindedMessage.id,
        `Mint signature keyset id at index ${i} does not match output: expected ${outputData[i].blindedMessage.id}, got ${signatures[i].id}. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
      );
      this.failIf(
        checkAmounts && !signatures[i].amount.equals(outputData[i].blindedMessage.amount),
        `Mint returned signature with wrong amount at index ${i}: expected ${outputData[i].blindedMessage.amount.toString()}, got ${signatures[i].amount.toString()}. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
      );
      this.failIf(
        requiresDleq && !signatures[i].dleq,
        `Mint supports NUT-12, but returned a signature without DLEQ proof at index ${i}. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
      );
    }
  }

  private validateMintQuoteAvailableAmount(
    method: string,
    quote: Pick<MintQuoteBaseResponse, 'quote'>,
    requestedAmount: Amount,
  ): void {
    if (method !== 'bolt12' && method !== 'onchain') {
      return;
    }
    if (!('amount_paid' in quote) || !('amount_issued' in quote)) {
      return;
    }
    const amountPaid = Amount.from(quote.amount_paid as AmountLike);
    const amountIssued = Amount.from(quote.amount_issued as AmountLike);
    const availableAmount = amountPaid.subtract(amountIssued);
    this.failIf(
      requestedAmount.greaterThan(availableAmount),
      `Mint quote ${quote.quote} has only ${availableAmount.toString()} available to mint; requested ${requestedAmount.toString()}`,
      {
        method,
        amount_paid: amountPaid.toString(),
        amount_issued: amountIssued.toString(),
        requestedAmount: requestedAmount.toString(),
      },
    );
  }

  /**
   * @internal
   */
  validateMintQuote(quote: Partial<MintQuoteBaseResponse> & { expiry?: number | null }): void {
    this.failIf(
      'unit' in quote && typeof quote.unit === 'string' && quote.unit !== this.unit,
      `Quote unit '${quote.unit}' does not match wallet unit '${this.unit}'`,
    );
    this.failIf(
      'expiry' in quote &&
        typeof quote.expiry === 'number' &&
        quote.expiry > 0 && // some mints (e.g. CDK) emit 0 for "no expiry"; spec says null
        quote.expiry < Math.floor(Date.now() / 1000),
      `Mint quote ${quote.quote} has expired`,
    );
  }

  /**
   * @internal
   */
  validateMeltQuote(quote: Partial<MeltQuoteBaseResponse>): void {
    this.failIf(
      'unit' in quote && typeof quote.unit === 'string' && quote.unit !== this.unit,
      `Quote unit '${quote.unit}' does not match wallet unit '${this.unit}'`,
    );
  }

  /**
   * Mints proofs for any payment method.
   *
   * @remarks
   * Generic convenience method that calls `prepareMint(method, …)` followed by `completeMint()`.
   *
   * Intended primarily for custom payment methods. For first-class methods, prefer
   * `mintProofsBolt11()` or `mintProofsBolt12()`, which keep the method-specific wallet ergonomics
   * and validation.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param amount Amount to mint.
   * @param quote The mint quote object (must have at least a `quote` field).
   * @param config Optional parameters (e.g. privkey for locked quotes).
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns Minted proofs.
   */
  async mintProofs<TQuote extends Pick<MintQuoteBaseResponse, 'quote'>>(
    method: string,
    amount: AmountLike,
    quote: TQuote,
    config?: MintProofsConfig,
    outputType?: OutputType,
  ): Promise<Proof[]> {
    const preview = await this.prepareMint(method, amount, quote, config, outputType);
    return this.completeMint(preview);
  }

  /**
   * Mint proofs for a bolt11 quote.
   *
   * @remarks
   * Convenience helper for the common BOLT11 flow. Internally this uses `prepareMint('bolt11',…)`
   * followed by `completeMint()`. Use `prepareMint()` directly when you need the generic method
   * based API or want to persist a replay-safe preview before completion.
   * @param amount Amount to mint.
   * @param quote Mint quote ID or object (bolt11).
   * @param config Optional parameters (e.g. privkey for locked quotes).
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns Minted proofs.
   */
  async mintProofsBolt11(
    amount: AmountLike,
    quote: string | MintQuoteBolt11Response,
    config?: MintProofsConfig,
    outputType?: OutputType,
  ): Promise<Proof[]> {
    this.requireSupport('mint', 'bolt11');
    if (typeof quote === 'string') {
      // Skip checkMintQuoteBolt11 to avoid an extra round-trip. This method returns Proof[]
      // so the quote object is never exposed to the caller — a stub is sufficient and
      // an invalid quote will be exposed in the minting step.
      const quoteObj = { quote };
      const preview = await this.prepareMint('bolt11', amount, quoteObj, config, outputType);
      return this.completeMint(preview);
    }
    this.validateMintQuote(quote);
    const preview = await this.prepareMint('bolt11', amount, quote, config, outputType);
    return this.completeMint(preview);
  }

  /**
   * Mints proofs for a bolt12 quote.
   *
   * @remarks
   * Convenience helper for the common BOLT12 flow. Internally this uses `prepareMint('bolt12',…)`
   * followed by `completeMint()`. Use `prepareMint()` directly when you need the generic method
   * based API or want to persist a replay-safe preview before completion.
   * @param amount Amount to mint.
   * @param quote Bolt12 mint quote.
   * @param privkey Private key to unlock the quote.
   * @param config Optional parameters (e.g. keysetId).
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns Minted proofs.
   */
  async mintProofsBolt12(
    amount: AmountLike,
    quote: MintQuoteBolt12Response,
    privkey: string,
    config?: { keysetId?: string },
    outputType?: OutputType,
  ): Promise<Proof[]> {
    this.requireSupport('mint', 'bolt12');
    const preview = await this.prepareMint(
      'bolt12',
      amount,
      quote,
      { ...config, privkey },
      outputType,
    );
    return this.completeMint(preview);
  }

  /**
   * Mints proofs using an onchain quote.
   *
   * @remarks
   * Convenience helper for the onchain flow. Pubkey is always required for onchain mint quotes, so
   * `privkey` is always needed. Use `prepareMint()` directly when you need the generic method based
   * API or want to persist a replay-safe preview before completion.
   * @param amount Amount to mint.
   * @param quote Onchain mint quote.
   * @param privkey Private key matching the pubkey the quote is locked to.
   * @param config Optional parameters (e.g. keysetId).
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns Minted proofs.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async mintProofsOnchain(
    amount: AmountLike,
    quote: MintQuoteOnchainResponse,
    privkey: string,
    config?: { keysetId?: string },
    outputType?: OutputType,
  ): Promise<Proof[]> {
    this.requireSupport('mint', 'onchain');
    const preview = await this.prepareMint(
      'onchain',
      amount,
      quote,
      { ...config, privkey },
      outputType,
    );
    return this.completeMint(preview);
  }

  /**
   * Prepare a mint transaction for replay and later completion.
   *
   * @remarks
   * Generic method-oriented mint API. This supports current methods such as `bolt11` and `bolt12`
   * as well as future custom methods exposed by the mint. For first-class typed ergonomics with the
   * built-in methods, prefer `mintProofsBolt11()`, `mintProofsBolt12()`, or
   * `wallet.ops.mintBolt11()/mintBolt12()`.
   *
   * Returns a `MintPreview` that contains the exact mint payload and output data needed to
   * construct proofs. Persist this preview to support NUT-19 replay safety.
   * @param quote The mint quote. Only `quote` (ID) is required — a full `MintQuoteBolt11Response`
   *   works, but `{ quote: string }` is sufficient. Pass `config.privkey` to produce a NUT-20
   *   signature regardless of whether the quote carries a `pubkey` field.
   */
  async prepareMint<TQuote extends Pick<MintQuoteBaseResponse, 'quote'>>(
    method: string,
    amount: AmountLike,
    quote: TQuote,
    config?: MintProofsConfig,
    outputType?: OutputType,
  ): Promise<MintPreview<TQuote>> {
    this.failIf(
      typeof quote === 'string',
      `prepareMint: expected a quote object, not a string ID. Use mintBolt11() which accepts string quote IDs.`,
    );
    this.validateMintQuote(quote);
    const requestedAmount = this.parseAmount(amount, `prepareMint: ${method}`);
    this.validateMintQuoteAvailableAmount(method, quote, requestedAmount);
    outputType = outputType ?? this.defaultOutputType(); // Fallback to policy
    const { privkey, keysetId, proofsWeHave, onCountersReserved } = config ?? {};

    // Shape output type and denominations for our proofs
    // we are receiving, so no includeFees.
    const keyset = this.getOutputKeyset(keysetId); // specified or wallet keyset
    let mintOT = this.configureOutputs(
      requestedAmount,
      keyset,
      outputType,
      false, // no fees
      proofsWeHave,
    );
    const mintAmount = this.preparedTotal(mintOT);

    // Assign counters atomically if OutputType is deterministic
    // and the counter is zero (auto-assign)
    const autoCounters = await this.addCountersToOutputTypes(keyset.id, mintOT);
    [mintOT] = autoCounters.outputTypes;
    if (autoCounters.used) {
      this.safeCallback(onCountersReserved, autoCounters.used, { op: 'mintProofs' });
    }
    this._logger.debug('mint counter', {
      counter: autoCounters.used,
      mintOT: stringifyOutputTypeForLog(mintOT),
    });

    // Create outputs and mint payload
    const outputs = this.createOutputData(mintAmount, keyset, mintOT);
    const blindedMessages = outputs.map((d) => d.blindedMessage);
    const mintPayload: MintRequest = {
      outputs: blindedMessages,
      quote: quote.quote,
    };

    // Require a privkey when the quote is known to be locked
    if ('pubkey' in quote && quote.pubkey) {
      this.failIf(!privkey, 'Can not sign locked quote without private key');
    }
    // Sign whenever a privkey is provided — quote.pubkey may be absent if only the
    // quote ID was stored, but the caller still needs to produce a NUT-20 signature
    if (privkey) {
      const quotePubkey = 'pubkey' in quote ? (quote.pubkey as string | undefined) : undefined;
      this.failIf(
        !quotePubkey && Array.isArray(privkey),
        `prepareMint: multiple privkeys supplied for quote '${quote.quote}' without pubkey`,
      );
      const signingKey = quotePubkey
        ? findSigningKey(quotePubkey, privkey)
        : Array.isArray(privkey)
          ? privkey[0]
          : privkey;
      this.failIf(!signingKey, 'prepareMint: privkey is empty or correct privkey not provided');
      mintPayload.signature = signMintQuote(signingKey, quote.quote, blindedMessages);
    }

    return {
      method,
      payload: mintPayload,
      outputData: outputs,
      keysetId: keyset.id,
      quote,
    };
  }

  /**
   * Complete a prepared mint transaction.
   *
   * @remarks
   * Use with a `MintPreview` returned by `prepareMint()`. This is the second step of the generic
   * mint flow and is also what the named convenience helpers use internally.
   * @param mintPreview Preview returned by prepareMint.
   * @returns Minted proofs.
   */
  async completeMint(
    mintPreview: MintPreview<Pick<MintQuoteBaseResponse, 'quote'>>,
  ): Promise<Proof[]> {
    const { payload, outputData, keysetId, method } = mintPreview;
    const { signatures } = await this.mint.mint(method, payload);
    this.failIf(
      signatures.length !== outputData.length,
      `Mint returned ${signatures.length} signatures, expected ${outputData.length}. The mint quote may already be marked issued; if the wallet is seeded, try restoring (NUT-09) to recover.`,
    );
    this.validateReturnedSignatures(signatures, outputData);

    const keyset = this.getKeyset(keysetId);
    this._logger.debug('MINT COMPLETED', {
      amounts: outputData.map((o) => o.blindedMessage.amount.toString()),
    });
    return outputData.map((d, i) => d.toProof(signatures[i], keyset));
  }

  /**
   * Prepare a batched mint transaction (NUT-29).
   *
   * @remarks
   * Creates a single consolidated set of outputs for all quotes.
   *
   * NOTE:
   *
   * - Any quote without a pubkey is considered unlocked. Pass `pubkey` for locked quotes.
   * - Check all quotes are in the PAID state. If any quote is unpaid, the entire batch with fail.
   *
   * @param method Payment method identifier (e.g., 'bolt11', 'bolt12').
   * @param entries Array of per-quote parameters: `{ amount, quote }`.
   * @param config Optional config applied to the entire batch (keysetId, privkey, counters, etc.).
   * @param outputType Optional output type override applied to the consolidated outputs.
   * @returns A `BatchMintPreview` ready to pass to `completeBatchMint`.
   * @experimental only supported by CDK mint >= 0.16.0
   */
  async prepareBatchMint<TQuote extends Pick<MintQuoteBaseResponse, 'quote' | 'pubkey'>>(
    method: string,
    entries: Array<{
      amount: AmountLike;
      quote: TQuote;
    }>,
    config?: MintProofsConfig,
    outputType?: OutputType,
  ): Promise<BatchMintPreview<TQuote>> {
    this.failIf(entries.length === 0, 'prepareBatchMint: no entries provided');

    // Enforce NUT-29 batch-size limit advertised by the mint, clamped to our absolute cap.
    // If the mint does not advertise NUT-29 info, the absolute cap still applies.
    const nut29 = this._mintInfo?.isSupported(29);
    const nut29Params = nut29?.supported ? nut29.params : undefined;

    const effectiveLimit = nut29Params?.max_batch_size ?? ABSOLUTE_MAX_BATCH_SIZE;

    if (entries.length > effectiveLimit) {
      const limitSource =
        nut29Params?.max_batch_size != null ? `mint's advertised limit` : `cashu-ts internal cap`;
      this.failIf(
        true,
        `prepareBatchMint: batch size ${entries.length} exceeds ` +
          `${limitSource} of ${effectiveLimit}`,
      );
    }

    // Warn if the requested method is not in the mint's NUT-29 supported methods
    if (nut29Params?.methods?.length) {
      if (!nut29Params.methods.includes(method)) {
        this._logger.warn(
          `prepareBatchMint: method '${method}' is not in mint's advertised NUT-29 methods`,
        );
      }
    }

    const { privkey, keysetId, proofsWeHave, onCountersReserved } = config ?? {};

    // Validate all quotes
    for (const entry of entries) {
      this.failIf(
        typeof entry.quote === 'string',
        `prepareBatchMint: expected a quote object, not a string ID`,
      );
      this.validateMintQuote(entry.quote);
    }

    // Check locked quotes: require a privkey and verify it can sign
    const hasLockedQuotes = entries.some((e) => 'pubkey' in e.quote && e.quote.pubkey);
    if (hasLockedQuotes) {
      this.failIf(!privkey, 'Can not sign locked quotes without private key');
    }

    // Parse amounts and determine keyset
    const keyset = this.getOutputKeyset(keysetId);
    const amounts = entries.map((e) => this.parseAmount(e.amount, `prepareBatchMint: ${method}`));
    const totalAmount = Amount.sum(amounts);

    // Shape consolidated outputs over the total amount
    outputType = outputType ?? this.defaultOutputType();
    let mintOT = this.configureOutputs(
      totalAmount,
      keyset,
      outputType,
      false, // no fees
      proofsWeHave,
    );

    // Assign counters atomically if deterministic
    const autoCounters = await this.addCountersToOutputTypes(keyset.id, mintOT);
    [mintOT] = autoCounters.outputTypes;
    if (autoCounters.used) {
      this.safeCallback(onCountersReserved, autoCounters.used, { op: 'mintProofs' });
    }

    // Create consolidated output data
    const outputs = this.createOutputData(totalAmount, keyset, mintOT);
    const blindedMessages = outputs.map((d) => d.blindedMessage);

    // Sign each locked quote over ALL blinded messages (NUT-29).
    // Unlocked quotes get null. If no quotes are locked, omit signatures entirely.
    // Each locked quote's pubkey is matched against the provided privkey(s).
    const signatures: Array<string | null> = [];
    let hasSignatures = false;
    for (const entry of entries) {
      const quotePubkey = 'pubkey' in entry.quote ? entry.quote.pubkey : undefined;
      if (quotePubkey && privkey) {
        const signingKey = findSigningKey(quotePubkey, privkey);
        signatures.push(signMintQuote(signingKey, entry.quote.quote, blindedMessages));
        hasSignatures = true;
      } else {
        if (privkey && !quotePubkey) {
          this._logger.warn(
            `prepareBatchMint: privkey supplied but quote '${entry.quote.quote}' has no pubkey — treating as unlocked`,
          );
        }
        signatures.push(null);
      }
    }

    const batchPayload: BatchMintRequest = {
      quotes: entries.map((e) => e.quote.quote),
      quote_amounts: amounts,
      outputs: blindedMessages,
      ...(hasSignatures ? { signatures } : {}),
    };

    return {
      method,
      payload: batchPayload,
      outputData: outputs,
      keysetId: keyset.id,
      quotes: entries.map((e) => e.quote),
    };
  }

  /**
   * Complete a prepared batch mint transaction.
   *
   * @remarks
   * Use with a `BatchMintPreview` returned by `prepareBatchMint()`.
   * @param batchPreview Preview returned by prepareBatchMint.
   * @returns Minted proofs.
   * @experimental only supported by CDK mint >= 0.16.0
   */
  async completeBatchMint(
    batchPreview: BatchMintPreview<Pick<MintQuoteBaseResponse, 'quote'>>,
  ): Promise<Proof[]> {
    const { method, payload, outputData, keysetId } = batchPreview;

    const { signatures: sigs } = await this.mint.mintBatch(method, payload);
    this.failIf(
      sigs.length !== outputData.length,
      `Mint returned ${sigs.length} signatures, expected ${outputData.length}. The mint quote may already be marked issued; if the wallet is seeded, try restoring (NUT-09) to recover.`,
    );
    this.validateReturnedSignatures(sigs, outputData);

    const keyset = this.getKeyset(keysetId);
    this._logger.debug('BATCH MINT COMPLETED', {
      quotes: payload.quotes.length,
      amounts: outputData.map((o) => o.blindedMessage.amount.toString()),
    });
    return outputData.map((d, i) => d.toProof(sigs[i], keyset));
  }

  // -----------------------------------------------------------------
  // Section: Create Melt Quote
  // -----------------------------------------------------------------

  /**
   * Creates a melt quote for any payment method.
   *
   * @remarks
   * Generic method for requesting a melt quote. The payload is method-specific but must at minimum
   * include the fields required by the mint for the given method. An optional `normalize` callback
   * can be used to coerce method-specific response fields.
   *
   * For first-class methods, prefer the typed helpers: `createMeltQuoteBolt11()`,
   * `createMeltQuoteBolt12()`.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param payload The request body to POST. `unit` is always set to the wallet's unit.
   * @param options.normalize Optional callback to normalize method-specific response fields.
   * @returns The melt quote response.
   */
  async createMeltQuote<TRes extends MeltQuoteBaseResponse = MeltQuoteBaseResponse>(
    method: string,
    payload: Record<string, unknown>,
    options?: { normalize?: (raw: Record<string, unknown>) => TRes },
  ): Promise<TRes> {
    const body = { ...payload, unit: this._unit };
    const res = await this.mint.createMeltQuote<TRes>(method, body, {
      normalize: options?.normalize,
    });
    return { ...res, unit: res.unit || this._unit };
  }

  /**
   * Requests a melt quote from the mint. Response returns amount and fees for a given unit in order
   * to pay a Lightning invoice.
   *
   * @param invoice LN invoice that needs to get a fee estimate.
   * @param amountMsat Optional amount in millisatoshis to attach for amountless invoices, must not
   *   be provided for invoices that already encode an amount.
   * @returns The mint will create and return a melt quote for the invoice with an amount and fee
   *   reserve.
   */
  async createMeltQuoteBolt11(
    invoice: string,
    amountMsat?: AmountLike,
  ): Promise<MeltQuoteBolt11Response> {
    this.requireSupport('melt', 'bolt11');
    const normalizedAmountMsat =
      amountMsat !== undefined ? this.parseAmount(amountMsat, 'createMeltQuoteBolt11') : undefined;

    if (amountMsat !== undefined) {
      this.failIf(
        invoiceHasAmountInHRP(invoice),
        'amountMsat supplied but invoice already contains an amount. Leave amountMsat undefined for non-zero invoices.',
      );
    }

    const supportsAmountless = this._mintInfo?.supportsAmountless?.('bolt11', this._unit) ?? false;

    const meltQuotePayload: MeltQuoteBolt11Request = {
      unit: this._unit,
      request: invoice,

      ...(supportsAmountless && normalizedAmountMsat !== undefined
        ? {
            options: {
              amountless: {
                amount_msat: normalizedAmountMsat,
              },
            },
          }
        : {}),
    };
    const meltQuote = await this.mint.createMeltQuoteBolt11(meltQuotePayload);
    return {
      ...meltQuote,
      unit: meltQuote.unit || this._unit,
      request: meltQuote.request || invoice,
    };
  }

  /**
   * Requests a melt quote from the mint. Response returns amount and fees for a given unit in order
   * to pay a BOLT12 offer.
   *
   * @param offer BOLT12 offer that needs to get a fee estimate.
   * @param amountMsat Amount in millisatoshis for amount-less offers. If this is defined and the
   *   offer has an amount, they **MUST** be equal.
   * @returns The mint will create and return a melt quote for the offer with an amount and fee
   *   reserve.
   */
  async createMeltQuoteBolt12(
    offer: string,
    amountMsat?: AmountLike,
  ): Promise<MeltQuoteBolt12Response> {
    this.requireSupport('melt', 'bolt12');
    const normalizedAmountMsat =
      amountMsat !== undefined ? this.parseAmount(amountMsat, 'createMeltQuoteBolt12') : undefined;
    return this.mint.createMeltQuoteBolt12({
      unit: this._unit,
      request: offer,
      options: normalizedAmountMsat
        ? {
            amountless: {
              amount_msat: normalizedAmountMsat,
            },
          }
        : undefined,
    });
  }

  /**
   * Requests an onchain melt quote from the mint.
   *
   * @param address Bitcoin address to send to.
   * @param amount Amount to melt.
   * @returns Melt quote with fee options.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async createMeltQuoteOnchain(
    address: string,
    amount: AmountLike,
  ): Promise<MeltQuoteOnchainResponse> {
    this.requireSupport('melt', 'onchain');
    const normalizedAmount = this.parseAmount(amount, 'createMeltQuoteOnchain');
    const quote = await this.mint.createMeltQuoteOnchain({
      unit: this._unit,
      request: address,
      amount: normalizedAmount,
    });
    return { ...quote, unit: quote.unit || this._unit };
  }

  /**
   * Requests a multi path melt quote from the mint.
   *
   * @remarks
   * Uses NUT-15 Partial multi-path payments for BOLT11.
   * @param invoice LN invoice that needs to get a fee estimate.
   * @param partialAmount The partial amount of the invoice's total to be paid by this instance.
   * @returns The mint will create and return a melt quote for the invoice with an amount and fee
   *   reserve.
   * @see https://github.com/cashubtc/nuts/blob/main/15.md
   */
  async createMultiPathMeltQuote(
    invoice: string,
    millisatPartialAmount: AmountLike,
  ): Promise<MeltQuoteBolt11Response> {
    const normalizedMillisatPartialAmount = this.parseAmount(
      millisatPartialAmount,
      'createMultiPathMeltQuote',
    );
    const { supported, params } = this.getMintInfo().isSupported(15);
    this.failIf(!supported, 'Mint does not support NUT-15');
    this.failIf(
      !params?.some((p) => p.method === 'bolt11' && p.unit === this._unit),
      `Mint does not support MPP for bolt11 and ${this._unit}`,
    );
    const meltQuotePayload: MeltQuoteBolt11Request = {
      unit: this._unit,
      request: invoice,
      options: { mpp: { amount: normalizedMillisatPartialAmount } },
    };
    const meltQuote = await this.mint.createMeltQuoteBolt11(meltQuotePayload);
    return { ...meltQuote, request: invoice, unit: this._unit };
  }

  // -----------------------------------------------------------------
  // Section: Check Melt Quote
  // -----------------------------------------------------------------

  /**
   * Checks an existing melt quote for any payment method.
   *
   * @remarks
   * Generic method for checking a melt quote status. An optional `normalize` callback can be used
   * to coerce method-specific response fields.
   *
   * For first-class methods, prefer the typed helpers: `checkMeltQuoteBolt11()`,
   * `checkMeltQuoteBolt12()`.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param quote Quote ID or quote object (must have a `quote` field).
   * @param options.normalize Optional callback to normalize method-specific response fields.
   * @returns The melt quote response.
   */
  async checkMeltQuote<TRes extends MeltQuoteBaseResponse = MeltQuoteBaseResponse>(
    method: string,
    quote: string | Pick<TRes, 'quote'>,
    options?: { normalize?: (raw: Record<string, unknown>) => TRes },
  ): Promise<TRes> {
    const quoteId = typeof quote === 'string' ? quote : (quote as { quote: string }).quote;
    return this.mint.checkMeltQuote<TRes>(method, quoteId, {
      normalize: options?.normalize,
    });
  }

  /**
   * Returns an existing bolt11 melt quote from the mint.
   *
   * @param quote ID of the melt quote.
   * @returns The mint will return an existing melt quote.
   */
  async checkMeltQuoteBolt11(
    quote: string | MeltQuoteBolt11Response,
  ): Promise<MeltQuoteBolt11Response> {
    const quoteId = typeof quote === 'string' ? quote : quote.quote;
    return this.mint.checkMeltQuoteBolt11(quoteId);
  }

  /**
   * Returns an existing bolt12 melt quote from the mint.
   *
   * @param quote ID of the melt quote.
   * @returns The mint will return an existing melt quote.
   */
  async checkMeltQuoteBolt12(quote: string): Promise<MeltQuoteBolt12Response> {
    return this.mint.checkMeltQuoteBolt12(quote);
  }

  /**
   * Returns an existing onchain melt quote from the mint.
   *
   * @param quote ID of the melt quote.
   * @returns The mint will return an existing melt quote.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async checkMeltQuoteOnchain(quote: string): Promise<MeltQuoteOnchainResponse> {
    return this.mint.checkMeltQuoteOnchain(quote);
  }

  // -----------------------------------------------------------------
  // Section: Melt Proofs
  // -----------------------------------------------------------------

  /**
   * Melts proofs for any payment method.
   *
   * @remarks
   * Generic convenience method that calls `prepareMelt(method, …)` followed by `completeMelt()`.
   *
   * Intended primarily for custom payment methods. For first-class methods, prefer
   * `meltProofsBolt11()` or `meltProofsBolt12()`, which keep the method-specific wallet
   * ergonomics.
   * @param method The payment method (e.g., 'bolt11', 'bolt12', 'bacs', 'swift').
   * @param meltQuote The melt quote object (must have at least `quote` and `amount` fields).
   * @param proofsToSend Proofs to melt.
   * @param config Optional parameters.
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns MeltProofsResponse with quote and change proofs.
   */
  async meltProofs<TQuote extends Pick<MeltQuoteBaseResponse, 'amount' | 'quote'>>(
    method: string,
    meltQuote: TQuote,
    proofsToSend: ProofLike[],
    config?: MeltProofsConfig,
    outputType?: OutputType,
  ): Promise<MeltProofsResponse<TQuote>> {
    const meltTxn = await this.prepareMelt(method, meltQuote, proofsToSend, config, outputType);
    return this.completeMelt<TQuote>(meltTxn, config?.privkey);
  }

  /**
   * Melt proofs for a bolt11 melt quote.
   *
   * @remarks
   * ProofsToSend must be at least amount+fee_reserve from the melt quote. This function does not
   * perform coin selection!.
   * @param meltQuote ID of the melt quote.
   * @param proofsToSend Proofs to melt.
   * @param config Optional parameters.
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns MeltProofsResponse with quote and change proofs.
   */
  async meltProofsBolt11(
    meltQuote: MeltQuoteBolt11Response,
    proofsToSend: ProofLike[],
    config?: MeltProofsConfig,
    outputType?: OutputType,
  ): Promise<MeltProofsResponse<MeltQuoteBolt11Response>> {
    this.requireSupport('melt', 'bolt11');
    const meltTxn = await this.prepareMelt('bolt11', meltQuote, proofsToSend, config, outputType);
    return this.completeMelt<MeltQuoteBolt11Response>(meltTxn, config?.privkey);
  }

  /**
   * Melt proofs for a bolt12 melt quote, returns change proofs using specified outputType.
   *
   * @remarks
   * ProofsToSend must be at least amount+fee_reserve from the melt quote. This function does not
   * perform coin selection!.
   * @param meltQuote ID of the melt quote.
   * @param proofsToSend Proofs to melt.
   * @param config Optional parameters.
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns MeltProofsResponse with quote and change proofs.
   */
  async meltProofsBolt12(
    meltQuote: MeltQuoteBolt12Response,
    proofsToSend: ProofLike[],
    config?: MeltProofsConfig,
    outputType?: OutputType,
  ): Promise<MeltProofsResponse<MeltQuoteBolt12Response>> {
    this.requireSupport('melt', 'bolt12');
    const meltTxn = await this.prepareMelt('bolt12', meltQuote, proofsToSend, config, outputType);
    return this.completeMelt<MeltQuoteBolt12Response>(meltTxn, config?.privkey);
  }

  /**
   * Melt proofs via an onchain Bitcoin transaction.
   *
   * @remarks
   * ProofsToSend must cover `quote.amount + selected fee + input fee`. Any additional amount is
   * offered as NUT-08 change. This function does not perform coin selection!
   *
   * Any `change` is only returned once the quote is `PAID` (mined and confirmed). Poll the quote
   * until broadcast, then unblind change with the returned `outputData` via
   * `wallet.createMeltChangeProofs()`.
   * @param meltQuote The onchain melt quote.
   * @param proofsToSend Proofs to melt.
   * @param feeIndex Selected `fee_index` value from `meltQuote.fee_options`.
   * @param config Optional parameters (e.g. privkey for P2PK proofs, keysetId).
   * @returns MeltProofsResponse with quote, any immediate change, and `outputData` for
   *   deferred-change reconstruction.
   * @experimental Onchain support follows NUT-30 semantics and may change.
   */
  async meltProofsOnchain(
    meltQuote: MeltQuoteOnchainResponse,
    proofsToSend: ProofLike[],
    feeIndex: number,
    config?: MeltProofsConfig,
  ): Promise<MeltProofsResponse<MeltQuoteOnchainResponse>> {
    this.requireSupport('melt', 'onchain');
    this.validateMeltQuote(meltQuote);
    // Validate fee_option selection
    const feeOption = meltQuote.fee_options.find((o) => o.fee_index === feeIndex);
    this.failIfNullish(feeOption, 'feeIndex must match an onchain melt quote fee option', {
      feeIndex,
      feeOptions: meltQuote.fee_options.map((o) => o.fee_index),
    });
    // Ensure we have enough proofs
    const normalizedProofs = normalizeProofAmounts(proofsToSend);
    const inputFee = this.getFeesForProofs(normalizedProofs);
    const sendAmount = sumProofs(normalizedProofs);
    const totalRequired = meltQuote.amount.add(feeOption.fee_reserve).add(inputFee);
    this.failIf(sendAmount.lessThan(totalRequired), 'Not enough proofs to cover amount + fee', {
      sendAmount: sendAmount.toString(),
      totalRequired: totalRequired.toString(),
      amount: meltQuote.amount.toString(),
      fee_reserve: feeOption.fee_reserve.toString(),
      inputFee: inputFee.toString(),
    });
    // Perform melt
    const meltTxn = await this.prepareMelt('onchain', meltQuote, normalizedProofs, config);
    const response = await this.completeMelt<MeltQuoteOnchainResponse>(meltTxn, config?.privkey, {
      extraPayload: { fee_index: feeIndex },
    });
    return response;
  }

  /**
   * Prepare A Melt Transaction.
   *
   * @remarks
   * Allows you to preview fees for a melt, get concrete outputs for P2PK SIG_ALL melts, and do any
   * pre-melt tasks (such as marking proofs in-flight etc). Creates NUT-08 blanks (1-sat) for melt
   * change and returns a MeltPreview, which you can melt using completeMelt.
   * @param method Payment method of the quote.
   * @param meltQuote The melt quote. Only `quote` (ID) and `amount` are required — a full
   *   `MeltQuoteBolt11Response` works, but `{ quote: string, amount: Amount }` is sufficient.
   * @param proofsToSend Proofs to melt.
   * @param config Optional configuration (keysetId, privkey, etc.).
   * @param outputType Configuration for proof generation. Defaults to wallet.defaultOutputType().
   * @returns MeltPreview.
   * @throws If params are invalid.
   * @see https://github.com/cashubtc/nuts/blob/main/08.md.
   */
  async prepareMelt<TQuote extends Pick<MeltQuoteBaseResponse, 'amount' | 'quote'>>(
    method: string,
    meltQuote: TQuote,
    proofsToSend: ProofLike[],
    config?: PrepareMeltConfig,
    outputType?: OutputType,
  ): Promise<MeltPreview<TQuote>> {
    this.validateMeltQuote(meltQuote);
    outputType = outputType ?? this.defaultOutputType(); // Fallback to policy
    const { keysetId, onCountersReserved, nut08Change = true } = config || {};
    const keyset = this.getOutputKeyset(keysetId); // specified or wallet keyset
    const normalizedProofs = normalizeProofAmounts(proofsToSend);
    const sendAmount = sumProofs(normalizedProofs);
    let outputData: OutputDataLike[] = [];

    // feeReserve is the overage above the invoice/offer amount.
    // In the common case where selected proofs = amount + fee_reserve,
    // this equals the quote’s fee_reserve. If you overshoot more,
    // the extra also becomes NUT-08 change, if melt method allows.
    this.failIf(
      sendAmount.lessThan(meltQuote.amount),
      'Not enough proofs to cover amount + fee reserve',
      {
        sendAmount: sendAmount.toString(),
        quoteAmount: meltQuote.amount.toString(),
      },
    );
    const feeReserve = sendAmount.subtract(meltQuote.amount);

    // Custom OT is definitive (no extra NUT-08 blanks)
    if (outputType.type === 'custom') {
      outputData = outputType.data;
    }

    // Create NUT-08 blanks for return of melt change (if supported)
    // Note: zero amount + zero denomination passes splitAmount validation
    else if (nut08Change && feeReserve.greaterThan(0)) {
      let count = Math.ceil(Math.log2(feeReserve.toNumberUnsafe())) || 1;
      if (count < 0) count = 0; // Prevents: -Infinity
      const denominations: number[] = count ? new Array<number>(count).fill(0) : [];
      this._logger.debug('Creating NUT-08 blanks for fee reserve', {
        feeReserve: feeReserve,
        denominations,
      });

      let meltOT: OutputType = { ...outputType, denominations };
      // Assign counter atomically if OutputType is deterministic
      // and the counter is zero (auto-assign)
      const autoCounters = await this.addCountersToOutputTypes(keyset.id, meltOT);
      [meltOT] = autoCounters.outputTypes;
      if (autoCounters.used) {
        this.safeCallback(onCountersReserved, autoCounters.used, { op: 'meltProofs' });
      }
      this._logger.debug('melt counter', {
        counter: autoCounters.used,
        meltOT: stringifyOutputTypeForLog(meltOT),
      });
      // Generate the blank outputs (no fees as we are receiving change)
      // Remember, zero amount + zero denomination passes splitAmount validation
      outputData = this.createOutputData(0, keyset, meltOT);
    }

    // Create melt preview
    const meltPreview: MeltPreview<TQuote> = {
      method,
      inputs: normalizedProofs,
      outputData,
      keysetId: keyset.id,
      quote: meltQuote,
    };

    return meltPreview;
  }

  /**
   * Completes a pending melt by calling the melt endpoint and constructing change proofs.
   *
   * @remarks
   * Use with a `MeltPreview` returned from `prepareMelt()`. This method lets you sign P2PK locked
   * proofs before melting. If the payment is pending or unpaid, the change array will be empty.
   * @param meltPreview The preview from prepareMelt().
   * @param privkey The private key(s) for signing.
   * @param options Optional override to request NUT-06 asynchronous melt or method-specific fields.
   * @returns Updated MeltProofsResponse.
   * @throws If melt fails or signatures don't match output count.
   */
  async completeMelt<TQuote extends Pick<MeltQuoteBaseResponse, 'quote'> = MeltQuoteBaseResponse>(
    meltPreview: MeltPreview<TQuote>,
    privkey?: string | string[],
    options?: CompleteMeltOptions,
  ): Promise<MeltProofsResponse<TQuote>>;
  /**
   * @deprecated Pass `{ preferAsync: true }` as the third param, instead of `true`.
   */
  async completeMelt<TQuote extends Pick<MeltQuoteBaseResponse, 'quote'> = MeltQuoteBaseResponse>(
    meltPreview: MeltPreview<TQuote>,
    privkey?: string | string[],
    preferAsync?: boolean,
  ): Promise<MeltProofsResponse<TQuote>>;
  async completeMelt<TQuote extends Pick<MeltQuoteBaseResponse, 'quote'> = MeltQuoteBaseResponse>(
    meltPreview: MeltPreview<TQuote>,
    privkey?: string | string[],
    options?: boolean | CompleteMeltOptions,
  ): Promise<MeltProofsResponse<TQuote>> {
    const completeOptions: CompleteMeltOptions =
      typeof options === 'boolean' ? { preferAsync: options } : (options ?? {});

    // Extract vars from MeltPreview
    let inputs = meltPreview.inputs;
    const outputs = meltPreview.outputData.map((d) => d.blindedMessage);
    const quote = meltPreview.quote.quote;

    // Sign proofs if needed
    if (privkey) {
      inputs = this.signP2PKProofs(inputs, privkey, meltPreview.outputData, quote);
    }

    // Prepare proofs for mint
    inputs = this._prepareInputsForMint(inputs);

    // Construct melt payload
    const meltPayload: MeltRequest = {
      quote,
      inputs,
      outputs,
      ...(completeOptions.preferAsync ? { prefer_async: true } : {}),
      ...completeOptions.extraPayload,
    };

    // Execute melt and validate result
    const meltResponse: MeltQuoteBaseResponse = await this.mint.melt<TQuote>(
      meltPreview.method,
      meltPayload,
    );

    // Create any change Proofs
    const change = this.createMeltChangeProofs(meltPreview.outputData, meltResponse.change ?? []);

    if (completeOptions.preferAsync) {
      this._logger.debug('ASYNC MELT REQUESTED', meltResponse);
    } else {
      this._logger.debug('MELT COMPLETED', {
        changeAmounts: change.map((p) => p.amount.toString()),
      });
    }

    // Merge preview quote with response to protect against incomplete response.
    // Retain outputData if no change was returned, so async/onchain can recover it later.
    const mergedQuote = { ...meltPreview.quote, ...meltResponse } as TQuote;
    return {
      quote: mergedQuote,
      change,
      outputData: change.length > 0 ? [] : meltPreview.outputData,
    };
  }

  /**
   * Constructs melt change proofs from prepared OutputData and mint returned Change Signatures.
   *
   * @remarks
   * Called internally by `completeMelt`; also useful for NUT-06 async melts and any other path that
   * defers change construction (crash recovery, process hand-off). Keyset lookup is per-signature
   * so multi-keyset responses (e.g. a permissive CDK mint) work transparently.
   * @param outputData Outputs from `prepareMelt()`, or deserialised persisted OutputData.
   * @param changeSigs The optional `change` signatures from the melt response or paid quote.
   * @returns Spendable change proofs (possibly empty).
   * @throws {@link CTSError} If signature count exceeds output count, any signature's keyset id
   *   does not match its paired output, or signatures cannot be verified.
   * @see {@link OutputData.serialize} for the persist/restore lifecycle example.
   */
  createMeltChangeProofs(
    outputData: OutputDataLike[],
    changeSigs: SerializedBlindedSignature[],
  ): Proof[] {
    // Change may not use all outputs (shorter is ok)
    this.failIf(
      changeSigs.length > outputData.length,
      `Mint returned ${changeSigs.length} signatures, but only ${outputData.length} blanks were provided. Inputs may already be spent; if the wallet is seeded, try restoring (NUT-09) to recover.`,
    );
    this.validateReturnedSignatures(changeSigs, outputData, {
      checkAmounts: false, // change outputs are blank
    });
    return changeSigs.map((s, i) => {
      let keyset: Keyset;
      try {
        keyset = this.getKeyset(s.id);
      } catch (e) {
        throw new CTSError(
          `Cannot reconstruct melt change: keyset ${s.id} is not loaded in this wallet (may be inactive after rotation). If the wallet is seeded, try restoring (NUT-09) to recover.`,
          { cause: e },
        );
      }
      return outputData[i].toProof(s, keyset);
    });
  }

  // -----------------------------------------------------------------
  // Section: Proof States
  // -----------------------------------------------------------------

  /**
   * Get an array of the states of proofs from the mint (as an array of CheckStateEnum's)
   *
   * @param proofs Each proof should carry both `secret` and `id`. The `id` is currently unused, but
   *   becomes required in v5 where it selects the hash-to-curve variant (secp256k1 vs BLS12-381).
   * @returns NUT-07 state for each proof, in same order.
   */
  async checkProofsStates(proofs: Array<Pick<ProofLike, 'secret' | 'id'>>): Promise<ProofState[]>;
  /**
   * @deprecated Include `id` on each proof alongside `secret`. The keyset `id` becomes required in
   *   v5 to select the hash-to-curve variant; `secret`-only proofs will stop working then.
   */
  async checkProofsStates(proofs: Array<Pick<Proof, 'secret'>>): Promise<ProofState[]>;
  async checkProofsStates(proofs: Array<Pick<ProofLike, 'secret'>>): Promise<ProofState[]> {
    const enc = new TextEncoder();
    const Ys = proofs.map((p: Pick<Proof, 'secret'>) =>
      hashToCurve(enc.encode(p.secret)).toHex(true),
    );
    // TODO: Replace this with a value from the info endpoint of the mint eventually
    const BATCH_SIZE = 100;
    const states: ProofState[] = [];
    for (let i = 0; i < Ys.length; i += BATCH_SIZE) {
      const YsSlice = Ys.slice(i, i + BATCH_SIZE);
      const { states: batchStates } = await this.mint.check({
        Ys: YsSlice,
      });
      const stateMap: { [y: string]: ProofState } = {};
      batchStates.forEach((s) => {
        stateMap[s.Y] = s;
      });
      for (let j = 0; j < YsSlice.length; j++) {
        const state = stateMap[YsSlice[j]];
        this.failIfNullish(state, 'Could not find state for proof with Y: ' + YsSlice[j]);
        states.push(state);
      }
    }
    return states;
  }

  /**
   * Groups proofs by their corresponding state, preserving order within each group.
   *
   * @remarks
   * Returns the same type you supply, eg: pass `MyProof[]` in, get `MyProof[]` back.
   * @param proofs Accepts {@link ProofLike} so proofs from storage work without conversion. Only the
   *   `secret` field is used for the state check; amounts are passed through as-is.
   * @returns An object with arrays of proofs grouped by CheckStateEnum state.
   */
  async groupProofsByState<T extends ProofLike = Proof>(
    proofs: T[],
  ): Promise<{ unspent: T[]; pending: T[]; spent: T[] }> {
    const states: ProofState[] = await this.checkProofsStates(proofs);
    const result = {
      unspent: [] as T[],
      pending: [] as T[],
      spent: [] as T[],
    };
    for (let i = 0; i < states.length; i++) {
      const proof = proofs[i];
      switch (states[i].state) {
        case CheckStateEnum.UNSPENT:
          result.unspent.push(proof);
          break;
        case CheckStateEnum.PENDING:
          result.pending.push(proof);
          break;
        case CheckStateEnum.SPENT:
          result.spent.push(proof);
          break;
      }
    }
    return result;
  }
}

export { Wallet };
